import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Modal, Button, Spin, Empty, Table, Tabs, Select, Drawer, Card, Divider, Tooltip, Checkbox, Tag } from 'antd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { 
  DownloadOutlined, 
  PrinterOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ScheduleOutlined,
  TeamOutlined,
  DragOutlined,
  CloseOutlined,
  SaveOutlined,
  SyncOutlined,
  SwapOutlined,
  QrcodeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { Typography } from 'antd';

const { TabPane } = Tabs;
const { Option } = Select;

const IntegratedClassManagement = () => {
  // 공통 상태
  const { authAxios, user } = useAuth();
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const [isLoading, setIsLoading] = useState(true);

  // 강의 관리 상태
  const [classes, setClasses] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [isAddingClass, setIsAddingClass] = useState(false);
  const [isEditingClass, setIsEditingClass] = useState(false);
  const [currentClass, setCurrentClass] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [currentQRCode, setCurrentQRCode] = useState('');
  const [qrLoading, setQrLoading] = useState(false);
  // 인라인 편집 관련 상태 추가
  const [editingField, setEditingField] = useState(null);
  const [editingClassId, setEditingClassId] = useState(null);
  const [editValue, setEditValue] = useState('');
  // 강의실 목록 상태 추가
  const [classrooms, setClassrooms] = useState([]);

  // 강의 구성 관리 상태
  const [activeTab, setActiveTab] = useState(() => {
    // 로컬 스토리지에서 저장된 탭 정보 불러오기
    return localStorage.getItem('integratedClassManagement_activeTab') || 'classes';
  });
  const [courseStructures, setCourseStructures] = useState([]);
  const [modules, setModules] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [activeStructureTab, setActiveStructureTab] = useState(null);
  const [showStructureForm, setShowStructureForm] = useState(false);
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [showProgramForm, setShowProgramForm] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  
  // 구성 및 모듈 폼 상태 추가
  const [newStructure, setNewStructure] = useState({ 
    name: '', 
    description: '', 
    order: 0, 
    isActive: true 
  });
  const [newModule, setNewModule] = useState({ 
    name: '', 
    structureType: '', 
    order: 0, 
    isActive: true 
  });
  const [newProgram, setNewProgram] = useState({
    name: '',
    moduleId: '',
    structureType: '',
    shortTermHours: 0,
    mediumTermHours: 0,
    longTermHours: 0,
    isActive: true
  });

  // 통합 기능 관련 상태
  const [showModuleSelector, setShowModuleSelector] = useState(false);
  const [selectedModuleForClass, setSelectedModuleForClass] = useState(null);
  const [selectedProgramsForClass, setSelectedProgramsForClass] = useState([]);
  const [showDragDropInterface, setShowDragDropInterface] = useState(false);

  // 컴포넌트 상단 상태에 추가
  const [selectedStructureForModule, setSelectedStructureForModule] = useState(null);

  // 데이터 로드 함수들
  const fetchClasses = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('강의 목록 불러오기 시작');
      const response = await authAxios.get('/classes');
      console.log(`서버로부터 ${response.data.length}개의 강의 데이터 수신`);
      
      // MongoDB _id를 id로 정규화
      const normalizedClasses = response.data.map(classItem => ({
        ...classItem,
        id: classItem._id || classItem.id
      }));
      
      console.log('강의 목록 업데이트:', normalizedClasses.length, '개 항목');
      setClasses(normalizedClasses);
      setIsLoading(false);
      return normalizedClasses; // 데이터 반환 추가
    } catch (err) {
      console.error('Error fetching classes:', err);
      toast.error('강의 목록을 불러오는 중 오류가 발생했습니다.');
      setIsLoading(false);
      return []; // 오류 시 빈 배열 반환
    }
  }, [authAxios]);

  const fetchInstructors = useCallback(async () => {
    try {
      const response = await authAxios.get('/users', {
        params: { role: 'instructor' }
      });
      
      // MongoDB _id를 id로 정규화
      const normalizedInstructors = response.data.map(instructor => ({
        ...instructor,
        id: instructor._id || instructor.id
      }));
      
      setInstructors(normalizedInstructors);
    } catch (err) {
      console.error('Error fetching instructors:', err);
      toast.error('강사 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }, [authAxios]);

  const fetchCourseStructures = useCallback(async () => {
    try {
      console.log('강의 구성 데이터 불러오기 시작');
      const response = await authAxios.get('/course-structures');
      
      // 응답 데이터 정렬 및 그룹화
      const { structures, modules, programs } = response.data;
      console.log('구성 데이터 수신:', {
        구성: structures?.length || 0,
        모듈: modules?.length || 0,
        프로그램: programs?.length || 0
      });
      
      // 데이터 정규화 (MongoDB _id를 id로 표준화)
      const normalizedStructures = structures?.map(structure => ({
        ...structure,
        id: structure.id || structure._id
      })) || [];
      
      const normalizedModules = modules?.map(module => ({
        ...module,
        id: module.id || module._id
      })) || [];
      
      const normalizedPrograms = programs?.map(program => ({
        ...program,
        id: program.id || program._id,
        moduleId: program.moduleId || program.module_id || program._moduleId
      })) || [];
      
      // 데이터 설정
      console.log('구성 데이터 상태 업데이트');
      setCourseStructures(normalizedStructures);
      setModules(normalizedModules);
      setPrograms(normalizedPrograms);
      
      // 첫 번째 구성을 기본 활성 탭으로 설정
      if (normalizedStructures.length > 0 && !activeStructureTab) {
        setActiveStructureTab(normalizedStructures[0].name);
      }
      
      // 데이터 반환 - 다른 함수에서 사용할 수 있도록
      return {
        structures: normalizedStructures,
        modules: normalizedModules,
        programs: normalizedPrograms
      };
    } catch (err) {
      console.error('Error fetching course structures:', err);
      toast.error('강의 구성 데이터를 불러오는 중 오류가 발생했습니다.');
      return null;
    }
  }, [authAxios, activeStructureTab]);

  // 강의실 목록 가져오기 함수 추가
  const fetchClassrooms = useCallback(async () => {
    try {
      const response = await authAxios.get('/classrooms');
      
      // MongoDB _id를 id로 정규화
      const normalizedClassrooms = response.data.map(classroom => ({
        ...classroom,
        id: classroom._id || classroom.id
      }));
      
      setClassrooms(normalizedClassrooms);
    } catch (err) {
      console.error('Error fetching classrooms:', err);
      toast.error('강의실 목록을 불러오는 중 오류가 발생했습니다.');
    }
  }, [authAxios]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    console.log('컴포넌트 마운트 시 데이터 로드');
    fetchClasses();
    fetchInstructors();
    fetchCourseStructures();
    fetchClassrooms(); // 강의실 목록 로드 추가
    
    // 주기적으로 강의 목록을 새로고침하는 타이머 설정 (60초마다)
    const refreshTimer = setInterval(() => {
      console.log('주기적 데이터 새로고침');
      fetchClasses();
      fetchCourseStructures();
    }, 60000);
    
    // 컴포넌트 언마운트 시 타이머 정리
    return () => clearInterval(refreshTimer);
  }, [fetchClasses, fetchInstructors, fetchCourseStructures, fetchClassrooms]);

  // 저장된 구성 탭 정보 로드
  useEffect(() => {
    const savedStructureTab = localStorage.getItem('integratedClassManagement_structureTab');
    if (savedStructureTab && courseStructures.some(structure => structure.name === savedStructureTab)) {
      setActiveStructureTab(savedStructureTab);
    } else if (courseStructures.length > 0 && !activeStructureTab) {
      setActiveStructureTab(courseStructures[0].name);
      localStorage.setItem('integratedClassManagement_structureTab', courseStructures[0].name);
    }
  }, [courseStructures, activeStructureTab]);
  
  // 컴포넌트 마운트 시 강의 구성 탭으로 설정
  useEffect(() => {
    setActiveTab('structures');
    localStorage.setItem('integratedClassManagement_activeTab', 'structures');
  }, []);

  // 강의 및 구성 통합 기능
  const getModulesByStructure = useCallback((structureType) => {
    return modules.filter(module => module.structureType === structureType);
  }, [modules]);

  // 프로그램 데이터와 강의 데이터를 통합하여 반환하는 함수 수정
  const getProgramsByModule = useCallback((moduleId) => {
    // 1. 기존 등록된 프로그램 가져오기
    const existingPrograms = programs.filter(program => program.moduleId === moduleId);
    
    // 2. 모듈 정보 가져오기
    const module = modules.find(m => m.id === moduleId);
    if (!module) return existingPrograms;
    
    // 6. 통합된 결과 반환
    return [...existingPrograms];
  }, [programs, modules]);

  // QR 코드 조회 함수
  const viewClassroomQRCode = async (classroomId) => {
    try {
      setQrLoading(true);
      
      // 강의실 QR 코드 조회 API 호출
      const response = await authAxios.get(`/classrooms/${classroomId}/qr-code`);
      
      // QR 코드 설정
      setCurrentQRCode(response.data.code);
      setCurrentClass(classes.find(c => c.classroomId === classroomId));
      setShowQRModal(true);
      
      toast.success('QR 코드를 조회했습니다.');
    } catch (err) {
      console.error('QR 코드 조회 오류:', err);
      toast.error('QR 코드 조회 중 오류가 발생했습니다.');
    } finally {
      setQrLoading(false);
    }
  };

  // 강의 삭제 함수
  const deleteClass = async (classId) => {
    try {
      // API 호출
      await authAxios.delete(`/classes/${classId}`);
      
      // 강의 목록 업데이트
      setClasses(prevClasses => prevClasses.filter(c => c.id !== classId));
      
      // 강의 구성 데이터도 새로고침
      fetchCourseStructures();
      
      toast.success('강의가 삭제되었습니다.');
    } catch (err) {
      console.error('강의 삭제 오류:', err);
      toast.error('강의 삭제 중 오류가 발생했습니다.');
    }
  };

  // 구성 삭제 함수
  const deleteStructure = async (structureId) => {
    try {
      // API 호출
      await authAxios.delete(`/course-structures/structures/${structureId}`);
      
      // 구성 목록 업데이트
      const updatedStructures = courseStructures.filter(s => s.id !== structureId);
      setCourseStructures(updatedStructures);
      
      // 삭제된 구성에 속한 모듈 및 프로그램 제거
      const deletedStructure = courseStructures.find(s => s.id === structureId);
      if (deletedStructure) {
        setModules(prevModules => prevModules.filter(m => m.structureType !== deletedStructure.name));
        setPrograms(prevPrograms => prevPrograms.filter(p => p.structureType !== deletedStructure.name));
      }
      
      // 전체 데이터 새로고침
      fetchCourseStructures();
      fetchClasses();
      
      toast.success('구성이 삭제되었습니다.');
      
      // 첫 번째 구성을 활성 탭으로 설정
      if (updatedStructures.length > 0) {
        setActiveStructureTab(updatedStructures[0].name);
      }
    } catch (err) {
      console.error('구성 삭제 오류:', err);
      toast.error('구성 삭제 중 오류가 발생했습니다.');
    }
  };

  // 모듈 삭제 함수
  const deleteModule = async (moduleId) => {
    try {
      // API 호출
      await authAxios.delete(`/course-structures/modules/${moduleId}`);
      
      // 모듈 목록 업데이트
      setModules(prevModules => prevModules.filter(m => m.id !== moduleId));
      
      // 모듈에 속한 프로그램 제거
      setPrograms(prevPrograms => prevPrograms.filter(p => p.moduleId !== moduleId));
      
      // 전체 데이터 새로고침
      fetchCourseStructures();
      fetchClasses();
      
      toast.success('모듈이 삭제되었습니다.');
    } catch (err) {
      console.error('모듈 삭제 오류:', err);
      toast.error('모듈 삭제 중 오류가 발생했습니다.');
    }
  };

  // 프로그램 편집 시작 함수
  const startEditProgram = (program, field) => {
    setEditingProgram(program);
    setEditingProgramField(field);
    setEditingProgramValue(program[field]);
  };
  
  // Enter 키 핸들러 추가
  const handleProgramEditKeyDown = (e, programId, field, value) => {
    if (e.key === 'Enter') {
      saveEditProgram(programId, field, value);
    } else if (e.key === 'Escape') {
      setEditingProgram(null);
      setEditingProgramField(null);
      setEditingProgramValue(null);
    }
  };

  // 프로그램 편집 필드와 값 상태
  const [editingProgram, setEditingProgram] = useState(null);
  const [editingProgramField, setEditingProgramField] = useState(null);
  const [editingProgramValue, setEditingProgramValue] = useState(null);

  // 프로그램 편집 저장 함수
  const saveEditProgram = async (programId, field, value) => {
    try {
      // 프로그램 찾기
      const program = programs.find(p => p.id === programId);
      
      // 프로그램이 강의에서 가져온 경우
      if (programId.startsWith('class-')) {
        toast.info('강의에서 가져온 데이터는 강의 관리에서 수정해주세요.');
        return;
      }
      
      if (!program) {
        toast.error('프로그램을 찾을 수 없습니다.');
        return;
      }

      // 필드에 따라 카테고리 결정
      let category;
      if (field === 'shortTermHours') {
        category = '단기';
      } else if (field === 'mediumTermHours') {
        category = '중기';
      } else if (field === 'longTermHours') {
        category = '장기';
      }
      
      // 시간 업데이트 API 호출
      await authAxios.put(`/course-structures/programs/${programId}`, {
        [field]: parseInt(value) || 0
      });
      
      // 프로그램 목록 업데이트
      setPrograms(prevPrograms => 
        prevPrograms.map(p => p.id === programId ? { ...p, [field]: parseInt(value) || 0 } : p)
      );
      
      // 전체 데이터 새로고침
      await Promise.all([
        fetchCourseStructures(),
        fetchClasses()
      ]);
      
      // 연결된 강의 데이터 새로고침
      if (program && program.moduleId) {
        // 모듈 관련 프로그램 데이터도 새로고침
        const relatedPrograms = programs.filter(p => p.moduleId === program.moduleId);
        for (const relatedProgram of relatedPrograms) {
          preloadLinkedClasses(relatedProgram.id);
        }
      }
      
      toast.success('시간이 업데이트되었습니다.');
    } catch (err) {
      console.error('프로그램 시간 업데이트 오류:', err);
      toast.error('시간 업데이트 중 오류가 발생했습니다.');
    }
  };

  // 프로그램 삭제 함수
  const deleteProgram = async (programId) => {
    try {
      // 강의에서 가져온 프로그램인 경우
      if (programId.startsWith('class-')) {
        toast.info('강의에서 가져온 데이터는 강의 관리에서 삭제해주세요.');
        return;
      }
      
      // API 호출
      await authAxios.delete(`/course-structures/programs/${programId}`);
      
      // 프로그램 목록 업데이트
      setPrograms(prevPrograms => prevPrograms.filter(p => p.id !== programId));
      
      // 전체 데이터 새로고침
      fetchCourseStructures();
      fetchClasses();
      
      toast.success('프로그램이 삭제되었습니다.');
    } catch (err) {
      console.error('프로그램 삭제 오류:', err);
      toast.error('프로그램 삭제 중 오류가 발생했습니다.');
    }
  };

  // 드래그 앤 드롭 종료 핸들러
  const handleDragEnd = async (result) => {
    // 드롭이 취소된 경우
    if (!result.destination) return;
    
    // 같은 위치에 드롭된 경우
    if (
      result.destination.droppableId === result.source.droppableId &&
      result.destination.index === result.source.index
    ) {
      return;
    }
    
    // 모듈을 강의에 연결하는 경우
    if (result.source.droppableId === 'modules' && result.destination.droppableId === 'classes') {
      // 드래그된 모듈 ID
      const moduleId = result.draggableId;
      const module = modules.find(m => m.id === moduleId);
      
      // 대상 강의(드롭된 위치의 강의)
      const classIndex = result.destination.index;
      const classItem = classes[classIndex];
      
      if (module && classItem) {
        try {
          // API 호출 - 강의에 모듈 연결
          await authAxios.put(`/classes/${classItem.id}`, {
            module: module.name,
            moduleId: module.id,
            structure: module.structureType,
            moduleInfo: {
              moduleId: module.id,
              moduleName: module.name,
              structureType: module.structureType
            }
          });
          
          // 강의 목록 업데이트
          setClasses(prevClasses => 
            prevClasses.map(c => 
              c.id === classItem.id 
                ? { 
                    ...c, 
                    module: module.name,
                    moduleId: module.id,
                    structure: module.structureType,
                    moduleInfo: {
                      moduleId: module.id,
                      moduleName: module.name,
                      structureType: module.structureType
                    }
                  } 
                : c
            )
          );
          
          toast.success(`'${module.name}' 모듈이 '${classItem.name}' 강의에 연결되었습니다.`);
        } catch (err) {
          console.error('모듈-강의 연결 오류:', err);
          toast.error('모듈과 강의 연결 중 오류가 발생했습니다.');
        }
      }
    }
  };

  // 모듈 선택기 열기
  const openModuleSelector = () => {
    setShowModuleSelector(true);
  };

  // 모듈 선택 처리
  const handleModuleSelect = (module) => {
    setSelectedModuleForClass(module);
    setValue('module', module.name);
    setValue('moduleId', module.id);
    setValue('structure', module.structureType);
    setShowModuleSelector(false);
  };

  // 프로그램 선택 처리
  const handleProgramsSelect = (selectedPrograms) => {
    setSelectedProgramsForClass(selectedPrograms);
    // 여기서 관련된 시간 정보도 계산할 수 있음
  };

  // 강의 추가 처리
  const onAddClass = async (data) => {
    try {
      setIsSubmitting(true);
      console.log('강의 추가 시작:', data.name);
      
      // 현재 사용자 정보
      const currentUserId = user?._id || user?.id;
      const currentUserName = user?.name || user?.username || '강사';
      
      // 날짜 포맷팅
      const classDate = new Date(data.date);
      
      // 시작 시간과 종료 시간을 Date 객체로 변환 (필수가 아닌 경우 처리)
      let startTimeDate = null;
      let endTimeDate = null;
      
      if (data.startTime) {
        const [startHour, startMinute] = data.startTime.split(':').map(Number);
        startTimeDate = new Date(classDate);
        startTimeDate.setHours(startHour, startMinute, 0);
      }
      
      if (data.endTime) {
        const [endHour, endMinute] = data.endTime.split(':').map(Number);
        endTimeDate = new Date(classDate);
        endTimeDate.setHours(endHour, endMinute, 0);
      }

      // 선택된 모듈 및 프로그램 정보 추가
      const moduleInfo = selectedModuleForClass 
        ? { 
            moduleId: selectedModuleForClass.id,
            moduleName: selectedModuleForClass.name,
            structureType: selectedModuleForClass.structureType,
            programs: selectedProgramsForClass.map(p => ({ 
              programId: p.id, 
              programName: p.name 
            }))
          }
        : null;

      // API에 전송할 데이터 구성
      const classData = {
        name: data.name,
        instructorId: data.instructorId || currentUserId,
        instructorName: data.instructorName || currentUserName,
        date: classDate.toISOString(),
        startTime: startTimeDate ? startTimeDate.toISOString() : null,
        endTime: endTimeDate ? endTimeDate.toISOString() : null,
        description: data.description || '',
        room: data.room || '',
        category: data.category || '단기',
        structure: data.structure || '공통',
        module: data.module || '',
        moduleId: selectedModuleForClass ? selectedModuleForClass.id : null,
        program: selectedProgramsForClass.length > 0 ? selectedProgramsForClass[0].name : '',
        programId: selectedProgramsForClass.length > 0 ? selectedProgramsForClass[0].id : null,
        moduleInfo: moduleInfo
      };
      
      console.log('API 호출 - 강의 데이터:', classData);
      
      // API 호출
      const response = await authAxios.post('/classes', classData);
      console.log('강의 생성 응답:', response.data);
      
      // 새로 생성된 강의의 ID
      const newClassId = response.data.id || response.data._id;
      console.log('새 강의 ID:', newClassId);
      
      // 전체 데이터 동기적으로 새로고침
      console.log('데이터 새로고침 시작');
      const [newClasses, newStructures] = await Promise.all([
        fetchClasses(),
        fetchCourseStructures()
      ]);
      console.log('데이터 새로고침 완료:', {
        강의목록: newClasses.length,
        구성데이터: newStructures ? '업데이트됨' : '오류'
      });
      
      // 직접 클래스 상태 업데이트 - 서버에서 받은 데이터 확인
      if (response.data && newClassId) {
        console.log('새 강의 정보를 상태에 직접 추가');
        setClasses(prevClasses => {
          // 이미 있는지 확인
          const exists = prevClasses.some(c => c.id === newClassId || c._id === newClassId);
          if (exists) {
            console.log('이미 강의가 상태에 존재함');
            return prevClasses;
          }
          
          // 없으면 새로 추가
          const normalizedClass = {
            ...response.data,
            id: newClassId
          };
          
          return [...prevClasses, normalizedClass];
        });
      }
      
      // 관련 모듈과 프로그램 데이터 갱신
      if (moduleInfo && moduleInfo.moduleId) {
        console.log('모듈 관련 프로그램 데이터 갱신');
        const relatedPrograms = programs.filter(p => p.moduleId === moduleInfo.moduleId);
        for (const program of relatedPrograms) {
          await preloadLinkedClasses(program.id);
        }
      }
      
      // 프로그램 연결 정보 초기화
      console.log('프로그램 연결 정보 초기화');
      setProgramLinkedClasses({});
      
      setIsAddingClass(false);
      reset();
      toast.success('강의가 성공적으로 추가되었습니다.');
      
      // 상태 초기화
      setSelectedModuleForClass(null);
      setSelectedProgramsForClass([]);
      
      // 강제 리렌더링을 위한 타이머 설정
      setTimeout(() => {
        fetchClasses();
      }, 500);
    } catch (err) {
      console.error('Add class error:', err);
      toast.error('강의 추가 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 탭 변경 처리
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // 로컬 스토리지에 현재 탭 저장
    localStorage.setItem('integratedClassManagement_activeTab', tabKey);
  };

  // 일반 인라인 편집 - input 요소 수정
  // ... existing code ...
  const startInlineEdit = (classId, field, currentValue) => {
    setEditingClassId(classId);
    setEditingField(field);
    setEditValue(currentValue || '');
  };

  // 인라인 편집 저장 함수
  const saveInlineEdit = async () => {
    if (!editingClassId || !editingField) return;

    try {
      // 현재 편집 중인 클래스 찾기
      const classToUpdate = classes.find(c => c.id === editingClassId);
      if (!classToUpdate) return;

      // API 호출을 위한 데이터 준비
      let updateData = {
        // 기존 필수 필드 보존
        startTime: classToUpdate.startTime,
        endTime: classToUpdate.endTime
      };

      // 특수 필드 처리
      if (editingField === 'moduleAndStructure') {
        // 코스(모듈)와 구성 동시 업데이트
        try {
          const parsedValue = JSON.parse(editValue);
          const selectedModule = modules.find(m => m.name === parsedValue.module);
          
          updateData.module = parsedValue.module;
          updateData.structure = parsedValue.structure;
          
          if (selectedModule) {
            updateData.moduleId = selectedModule.id;
            updateData.moduleInfo = {
              moduleId: selectedModule.id,
              moduleName: selectedModule.name,
              structureType: selectedModule.structureType
            };
          }
        } catch (e) {
          console.error('모듈 및 구성 데이터 파싱 오류:', e);
          throw new Error('데이터 형식이 올바르지 않습니다.');
        }
      } else if (editingField === 'module') {
        // 모듈 필드 업데이트 시 모듈 ID도 함께 설정
        const selectedModule = modules.find(m => m.name === editValue);
        if (selectedModule) {
          updateData.module = editValue;
          updateData.moduleId = selectedModule.id;
          updateData.structure = selectedModule.structureType;
          updateData.moduleInfo = {
            moduleId: selectedModule.id,
            moduleName: selectedModule.name,
            structureType: selectedModule.structureType
          };
        } else {
          updateData.module = editValue;
        }
      } else if (editingField === 'program') {
        // 프로그램 필드 업데이트
        updateData.program = editValue;
        
        // 현재 선택된 모듈에 해당하는 프로그램 중에서 선택된 프로그램 찾기
        if (classToUpdate.moduleId) {
          const selectedProgram = getProgramsByModule(classToUpdate.moduleId)
            .find(p => p.name === editValue);
          
          if (selectedProgram) {
            updateData.programId = selectedProgram.id;
          }
        }
      } else {
        // 일반 필드 업데이트
        updateData[editingField] = editValue;
      }

      // 날짜 필드 특수 처리
      if (editingField === 'date') {
        // 날짜 형식 표준화 (ISO 문자열로 변환)
        const dateObj = new Date(editValue);
        if (isNaN(dateObj.getTime())) {
          throw new Error('유효하지 않은 날짜 형식입니다.');
        }
        updateData.date = dateObj.toISOString();
      }

      // API 호출
      await authAxios.put(`/classes/${editingClassId}`, updateData);

      // 전체 데이터 새로고침
      await Promise.all([
        fetchClasses(),
        fetchCourseStructures()
      ]);
      
      // 해당 강의와 관련된 프로그램 데이터 갱신
      if (classToUpdate.moduleId) {
        const relatedPrograms = programs.filter(p => p.moduleId === classToUpdate.moduleId);
        for (const program of relatedPrograms) {
          preloadLinkedClasses(program.id);
        }
      }
      
      // 프로그램 연결 정보도 갱신
      setProgramLinkedClasses({});
      
      toast.success('강의 정보가 업데이트되었습니다.');
    } catch (err) {
      console.error('인라인 편집 저장 오류:', err);
      toast.error('정보 업데이트 중 오류가 발생했습니다.');
    } finally {
      // 상태 초기화
      setEditingClassId(null);
      setEditingField(null);
      setEditValue('');
      setSelectedStructureForModule(null);
    }
  };

  // 인라인 편집 취소 함수
  const cancelInlineEdit = () => {
    setEditingClassId(null);
    setEditingField(null);
    setEditValue('');
  };

  // Enter 키 핸들러 추가
  const handleInlineEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveInlineEdit();
    } else if (e.key === 'Escape') {
      cancelInlineEdit();
    }
  };

  // 구성 추가 핸들러
  const handleAddStructure = async () => {
    try {
      if (!newStructure.name) {
        toast.error('구성 이름은 필수 항목입니다.');
        return;
      }

      // API 호출
      const response = await authAxios.post('/course-structures/structures', newStructure);
      
      // 목록 업데이트
      setCourseStructures(prevStructures => [...prevStructures, response.data]);
      
      // 폼 초기화
      setNewStructure({ name: '', description: '', order: 0, isActive: true });
      setShowStructureForm(false);
      
      // 전체 데이터 새로고침
      await Promise.all([
        fetchCourseStructures(),
        fetchClasses()
      ]);
      
      // 프로그램 연결 정보 초기화
      setProgramLinkedClasses({});
      
      toast.success('구성이 추가되었습니다.');
      
      // 새로운 구성을 활성 탭으로 설정
      if (response.data && response.data.name) {
        setActiveStructureTab(response.data.name);
        localStorage.setItem('integratedClassManagement_structureTab', response.data.name);
      }
    } catch (err) {
      console.error('구성 추가 오류:', err);
      toast.error('구성 추가 중 오류가 발생했습니다.');
    }
  };

  // 모듈 추가 핸들러
  const handleAddModule = async () => {
    try {
      if (!newModule.name || !newModule.structureType) {
        toast.error('모듈 이름과 구성 타입은 필수 항목입니다.');
        return;
      }

      // API 호출
      const response = await authAxios.post('/course-structures/modules', newModule);
      
      // 목록 업데이트
      setModules([...modules, response.data]);
      
      // 폼 초기화
      setNewModule({ name: '', structureType: '', order: 0, isActive: true });
      setShowModuleForm(false);
      setSelectedStructure(null);
      
      // 전체 데이터 새로고침
      await Promise.all([
        fetchCourseStructures(),
        fetchClasses()
      ]);
      
      // 프로그램 연결 정보 초기화
      setProgramLinkedClasses({});
      
      toast.success('모듈이 추가되었습니다.');
    } catch (err) {
      console.error('모듈 추가 오류:', err);
      toast.error('모듈 추가 중 오류가 발생했습니다.');
    }
  };

  // 프로그램 추가 핸들러
  const handleAddProgram = async () => {
    try {
      console.log('프로그램 추가 시작:', {
        newProgram,
        selectedModule
      });

      if (!newProgram.name) {
        console.error('프로그램 이름 누락');
        toast.error('프로그램 이름은 필수 항목입니다.');
        return;
      }

      // 모듈 정보 가져오기
      const module = modules.find(m => m.id === newProgram.moduleId);
      console.log('선택된 모듈 정보:', module);

      if (!module) {
        console.error('유효하지 않은 모듈 ID:', newProgram.moduleId);
        toast.error('유효한 모듈을 선택해주세요.');
        return;
      }

      // API 호출 - 프로그램 추가
      console.log('프로그램 추가 API 요청 데이터:', {
        name: newProgram.name,
        moduleId: newProgram.moduleId,
        structureType: module.structureType,
        shortTermHours: newProgram.shortTermHours || 0,
        mediumTermHours: newProgram.mediumTermHours || 0,
        longTermHours: newProgram.longTermHours || 0,
        isActive: true
      });

      const response = await authAxios.post('/course-structures/programs', {
        name: newProgram.name,
        moduleId: newProgram.moduleId,
        structureType: module.structureType,
        shortTermHours: newProgram.shortTermHours || 0,
        mediumTermHours: newProgram.mediumTermHours || 0,
        longTermHours: newProgram.longTermHours || 0,
        isActive: true
      });
      
      console.log('프로그램 추가 API 응답:', response.data);
      
      // 목록 업데이트
      console.log('데이터 새로고침 시작');
      await Promise.all([
        fetchCourseStructures(),
        fetchClasses()
      ]);
      
      // 새로 생성된 프로그램 ID
      const newProgramId = response.data.id || response.data._id;
      
      // 모듈 관련 데이터 갱신
      if (newProgram.moduleId) {
        const relatedPrograms = programs.filter(p => p.moduleId === newProgram.moduleId);
        
        // 프로그램 연결 정보도 갱신
        for (const program of relatedPrograms) {
          preloadLinkedClasses(program.id);
        }
        
        // 새 프로그램 데이터도 초기화
        preloadLinkedClasses(newProgramId);
      }
      
      console.log('데이터 새로고침 완료');
      
      // 폼 초기화
      setNewProgram({
        name: '',
        moduleId: '',
        structureType: '',
        shortTermHours: 0,
        mediumTermHours: 0,
        longTermHours: 0,
        isActive: true
      });
      setShowProgramForm(false);
      setSelectedModule(null);
      
      toast.success('프로그램이 추가되었습니다.');
    } catch (err) {
      console.error('프로그램 추가 중 상세 오류:', {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });
      toast.error(`프로그램 추가 중 오류가 발생했습니다: ${err.response?.data?.message || err.message}`);
    }
  };

  // 강의 목록에서 프로그램 데이터 로드
  const getProgramsFromClasses = useCallback(() => {
    // 강의가 없는 경우 기존 프로그램 반환
    if (!classes.length) return programs;

    // 강의 데이터에서 프로그램 데이터 생성
    const classBasedPrograms = classes.map(classItem => ({
      id: classItem.id,
      name: classItem.name,
      moduleId: classItem.moduleInfo?.moduleId || '',
      structureType: classItem.structure || '',
      shortTermHours: classItem.category === '단기' ? 8 : 0,
      mediumTermHours: classItem.category === '중기' ? 8 : 0,
      longTermHours: classItem.category === '장기' ? 8 : 0,
      isActive: true
    }));

    // 기존 프로그램과 새 프로그램 합치기 (중복 제거)
    const existingIds = programs.map(p => p.id);
    const newPrograms = classBasedPrograms.filter(p => !existingIds.includes(p.id));
    
    return [...programs, ...newPrograms];
  }, [classes, programs]);

  // 구성 관리 탭을 수정
  const renderStructureForm = () => (
    <Modal
      title="새 구성 추가"
      visible={showStructureForm}
      onCancel={() => setShowStructureForm(false)}
      footer={[
        <Button key="cancel" onClick={() => setShowStructureForm(false)}>
          취소
        </Button>,
        <Button key="submit" type="primary" onClick={handleAddStructure}>
          저장
        </Button>
      ]}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            구성 이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newStructure.name}
            onChange={e => setNewStructure({ ...newStructure, name: e.target.value })}
            className="w-full border rounded-md p-2"
            placeholder="예: 공통, 지역, 특화"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            설명
          </label>
          <textarea
            value={newStructure.description}
            onChange={e => setNewStructure({ ...newStructure, description: e.target.value })}
            className="w-full border rounded-md p-2"
            rows="3"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            순서
          </label>
          <input
            type="number"
            value={newStructure.order}
            onChange={e => setNewStructure({ ...newStructure, order: parseInt(e.target.value) || 0 })}
            className="w-full border rounded-md p-2"
            min="0"
          />
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="isActive"
            checked={newStructure.isActive}
            onChange={e => setNewStructure({ ...newStructure, isActive: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="isActive" className="text-sm text-gray-700">
            활성화
          </label>
        </div>
      </div>
    </Modal>
  );

  // 모듈 추가 모달
  const renderModuleForm = () => (
    <Modal
      title={`모듈 추가 - ${selectedStructure?.name || ''}`}
      visible={showModuleForm}
      onCancel={() => {
        setShowModuleForm(false);
        setSelectedStructure(null);
      }}
      footer={[
        <Button 
          key="cancel" 
          onClick={() => {
            setShowModuleForm(false);
            setSelectedStructure(null);
          }}
        >
          취소
        </Button>,
        <Button key="submit" type="primary" onClick={handleAddModule}>
          저장
        </Button>
      ]}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            모듈 이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newModule.name}
            onChange={e => setNewModule({ ...newModule, name: e.target.value })}
            className="w-full border rounded-md p-2"
            placeholder="모듈 이름 입력"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            구성 타입 <span className="text-red-500">*</span>
          </label>
          <select
            value={newModule.structureType}
            onChange={e => setNewModule({ ...newModule, structureType: e.target.value })}
            className="w-full border rounded-md p-2"
            disabled={!!selectedStructure}
          >
            <option value="">-- 구성 선택 --</option>
            {courseStructures.map(structure => (
              <option key={structure.id} value={structure.name}>
                {structure.name}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            순서
          </label>
          <input
            type="number"
            value={newModule.order}
            onChange={e => setNewModule({ ...newModule, order: parseInt(e.target.value) || 0 })}
            className="w-full border rounded-md p-2"
            min="0"
          />
        </div>
        
        <div className="flex items-center">
          <input
            type="checkbox"
            id="moduleIsActive"
            checked={newModule.isActive}
            onChange={e => setNewModule({ ...newModule, isActive: e.target.checked })}
            className="mr-2"
          />
          <label htmlFor="moduleIsActive" className="text-sm text-gray-700">
            활성화
          </label>
        </div>
      </div>
    </Modal>
  );

  // 강의(프로그램) 추가 모달
  const renderProgramForm = () => (
    <Modal
      title={`프로그램 추가 - ${selectedModule?.name || ''}`}
      visible={showProgramForm}
      onCancel={() => {
        setShowProgramForm(false);
        setSelectedModule(null);
      }}
      footer={[
        <Button 
          key="cancel" 
          onClick={() => {
            setShowProgramForm(false);
            setSelectedModule(null);
          }}
        >
          취소
        </Button>,
        <Button key="submit" type="primary" onClick={handleAddProgram}>
          저장
        </Button>
      ]}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            프로그램 이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={newProgram.name}
            onChange={e => setNewProgram({ ...newProgram, name: e.target.value })}
            className="w-full border rounded-md p-2"
            placeholder="프로그램 이름 입력"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            모듈 <span className="text-red-500">*</span>
          </label>
          <select
            value={newProgram.moduleId}
            onChange={e => setNewProgram({ ...newProgram, moduleId: e.target.value })}
            className="w-full border rounded-md p-2"
            disabled={!!selectedModule}
          >
            <option value="">-- 모듈 선택 --</option>
            {modules.map(module => (
              <option key={module.id} value={module.id}>
                {module.name} ({module.structureType})
              </option>
            ))}
          </select>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              단기 시간
            </label>
            <input
              type="number"
              value={newProgram.shortTermHours}
              onChange={e => setNewProgram({ ...newProgram, shortTermHours: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md p-2"
              min="0"
            />
            <div className="mt-1 text-xs text-gray-500">
              단기 과정 필요 시간을 입력하세요.
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              중기 시간
            </label>
            <input
              type="number"
              value={newProgram.mediumTermHours}
              onChange={e => setNewProgram({ ...newProgram, mediumTermHours: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md p-2"
              min="0"
            />
            <div className="mt-1 text-xs text-gray-500">
              중기 과정 필요 시간을 입력하세요.
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              장기 시간
            </label>
            <input
              type="number"
              value={newProgram.longTermHours}
              onChange={e => setNewProgram({ ...newProgram, longTermHours: parseInt(e.target.value) || 0 })}
              className="w-full border rounded-md p-2"
              min="0"
            />
            <div className="mt-1 text-xs text-gray-500">
              장기 과정 필요 시간을 입력하세요.
            </div>
          </div>
        </div>

        <div className="mt-2">
          <div className="text-sm text-gray-700 font-medium mb-1">안내</div>
          <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
            프로그램의 시간 정보는 강의 계획 구성에만 사용됩니다.
            강의는 수동으로 생성 후 연결해야 합니다.
          </div>
        </div>
      </div>
    </Modal>
  );

  // 수강인원 관리 모달 상태 추가
  const [showStudentManagement, setShowStudentManagement] = useState(false);
  const [classStudents, setClassStudents] = useState([]);
  const [allStudents, setAllStudents] = useState([]); // 전체 수강생 목록 상태 추가
  const [isLoadingStudents, setIsLoadingStudents] = useState(false);
  
  // 존재하는 기수만 탭으로 표시하기 위한 계산
  const existingBatches = useMemo(() => {
    // 모든 수강생(필터링된)의 기수 추출
    const batchSet = new Set();
    allStudents.forEach(student => {
      if (student.batch) {
        batchSet.add(student.batch.toString());
      }
    });
    // 숫자 순으로 정렬
    return Array.from(batchSet).sort((a, b) => Number(a) - Number(b));
  }, [allStudents]);

  // 추가 정보 표시 (교육 분류 및 장소)
  const classInfo = useMemo(() => {
    if (!currentClass) return { category: '-', classroom: '-', classroomCategory: '-' };
    
    // 강의실 정보 찾기
    const classroom = classrooms.find(c => c._id === currentClass.classroomId);
    
    return {
      category: currentClass.category || '-',
      classroom: classroom?.name || '-',
      classroomCategory: classroom?.category || '-'
    };
  }, [currentClass, classrooms]);

  // 전체 수강생 목록 조회 함수 수정
  const fetchAllStudents = async () => {
    try {
      console.log('수강생 목록 조회 시작');
      console.log('현재 강의 정보:', currentClass);
      
      // 현재 강의 정보가 없으면 경고 로그 출력
      if (!currentClass) {
        console.warn('현재 선택된 강의 정보가 없습니다. 모든 학생이 표시됩니다.');
      }
      
      // 학생 목록 및 강의실 정보 병렬로 가져오기
      const [studentsResponse, classroomsResponse] = await Promise.all([
        authAxios.get('/students'),
        authAxios.get('/classrooms')
      ]);
      
      console.log('학생 API 응답 데이터:', studentsResponse.data);
      console.log('강의실 API 응답 데이터:', classroomsResponse.data);
      
      if (!Array.isArray(studentsResponse.data)) {
        console.error('학생 응답 데이터가 배열이 아님:', studentsResponse.data);
        throw new Error('학생 응답 데이터 형식이 올바르지 않습니다.');
      }
      
      // 강의실 정보 맵 구성 (ID -> 카테고리)
      const classroomCategoryMap = {};
      if (Array.isArray(classroomsResponse.data)) {
        classroomsResponse.data.forEach(classroom => {
          classroomCategoryMap[classroom._id] = classroom.category;
        });
      }
      
      console.log('강의실 카테고리 맵:', classroomCategoryMap);
      
      // 현재 강의의 강의실 카테고리 가져오기
      let currentClassroomCategory = null;
      if (currentClass && currentClass.classroomId) {
        currentClassroomCategory = classroomCategoryMap[currentClass.classroomId];
        console.log('현재 강의실 카테고리:', currentClassroomCategory);
      }
      
      // 데이터 정규화: MongoDB _id를 id로 표준화하고 null 체크
      const normalizedStudents = studentsResponse.data.map(student => ({
        ...student,
        id: student._id || student.id,
        name: student.name || '이름 없음',
        batch: student.batch || null,
        category: student.category || null,
        campus: student.campus || null
      }));
      
      // 기수별로 정렬
      const sortedStudents = normalizedStudents.sort((a, b) => {
        // batch 필드가 없는 경우 기본값 0으로 처리
        const batchA = Number(a.batch) || 0;
        const batchB = Number(b.batch) || 0;
        
        // 기수가 같으면 이름으로 정렬
        if (batchA === batchB) {
          return (a.name || '').localeCompare(b.name || '');
        }
        // 기수로 정렬
        return batchA - batchB;
      });
      
      // 현재 강의 정보를 확인하여 필터링 조건 설정
      const filterStudents = currentClass ? sortedStudents.filter(student => {
        // 디버깅을 위한 로그
        console.log('학생 필터링 검사:', {
          학생: {
            id: student.id,
            이름: student.name,
            카테고리: student.category,
            캠퍼스: student.campus
          },
          강의: {
            id: currentClass.id,
            이름: currentClass.name, 
            카테고리: currentClass.category,
            강의실ID: currentClass.classroomId,
            강의실카테고리: currentClassroomCategory
          }
        });
        
        // 필터링 로직:
        // 1. 카테고리 필터링 - student.category가 없으면 모든 카테고리에 포함
        //    또는 student.category가 있고 currentClass.category와 일치하면 포함
        const categoryMatch = !student.category || student.category === currentClass.category;
        
        // 2. 캠퍼스 필터링 - 강의실 카테고리와 학생 캠퍼스가 일치하는지 확인
        // - student.campus가 없으면 모든 캠퍼스에 포함
        // - 또는 현재 강의실의 카테고리가 없으면 모든 학생 포함
        // - 또는 student.campus와 currentClassroomCategory가 일치하면 포함
        const campusMatch = !student.campus || !currentClassroomCategory || 
                           student.campus === currentClassroomCategory;
        
        // 두 조건 모두 충족하면 포함 (AND 조건)
        return categoryMatch && campusMatch;
      }) : sortedStudents;
      
      console.log('필터링된 학생 목록:', filterStudents);
      setAllStudents(filterStudents);
    } catch (err) {
      console.error('대상 수강생 조회 상세 오류:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });
      toast.error('대상 수강생 목록을 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 수강인원 목록 조회 함수 수정
  const fetchClassStudents = async (classId) => {
    try {
      console.log('수강인원 목록 조회 시작 - 강의 ID:', classId);
      setIsLoadingStudents(true);
      
      // 먼저 전체 학생 목록 불러오기 (필터링이 적용된 학생 목록)
      await fetchAllStudents();
      
      const response = await authAxios.get(`/classes/${classId}/students`);
      console.log('수강인원 API 응답 데이터:', response.data);
      
      if (!Array.isArray(response.data)) {
        console.error('수강인원 응답 데이터가 배열이 아님:', response.data);
        throw new Error('수강인원 응답 데이터 형식이 올바르지 않습니다.');
      }
      
      // MongoDB _id 필드를 id로 표준화
      const normalizedEnrolledStudents = response.data.map(student => ({
        ...student,
        id: student._id || student.id
      }));
      
      // 전체 수강생 목록과 현재 강의 수강생 목록을 합쳐서 처리
      const enrolledStudentIds = new Set(normalizedEnrolledStudents.map(student => student.id));
      console.log('등록된 학생 ID 목록:', Array.from(enrolledStudentIds));
      
      // allStudents가 비어있으면 API 응답 데이터만 사용
      const studentsList = allStudents.length > 0 ? allStudents : normalizedEnrolledStudents;
      
      const combinedStudents = studentsList.map(student => {
        // MongoDB _id 필드 처리
        const studentId = student._id || student.id;
        return {
          ...student,
          id: studentId,
          isEnrolled: enrolledStudentIds.has(studentId)
        };
      });
      
      console.log('통합된 학생 목록:', combinedStudents);
      setClassStudents(combinedStudents);
    } catch (err) {
      console.error('수강인원 조회 상세 오류:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        stack: err.stack
      });
      toast.error('수강인원 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // 수강인원 관리 모달이 열릴 때 전체 수강생 목록도 함께 로드
  useEffect(() => {
    if (showStudentManagement) {
      fetchAllStudents();
    }
  }, [showStudentManagement]);

  // 수강생 추가/제거 통합 함수 (ClassManagement.js 참조)
  const toggleStudentEnrollment = async (classId, studentId, isEnrolled) => {
    console.log('toggleStudentEnrollment 호출됨:', { classId, studentId, isEnrolled });
    try {
      if (!classId || !studentId) {
        console.error('필수 매개변수 누락:', { classId, studentId });
        return toast.error('유효하지 않은 요청입니다.');
      }
      
      setIsLoadingStudents(true);
      
      if (isEnrolled) {
        // 이미 등록된 학생은 제거
        console.log('학생 제거 API 요청:', `classes/${classId}/students/${studentId}`);
        const response = await authAxios.delete(`/classes/${classId}/students/${studentId}`);
        console.log('학생 제거 API 응답:', response);
        
        // API 호출이 성공한 경우에만 UI 업데이트
        if (response.status === 200 || response.status === 204) {
          // 전체 학생 목록 업데이트 (isEnrolled 플래그 변경)
          setClassStudents(prevStudents => {
            console.log('수강생 제거 전 목록:', prevStudents);
            const updatedStudents = prevStudents.map(student => {
              const studentIdMatches = student.id === studentId || student._id === studentId;
              return studentIdMatches
                ? { ...student, isEnrolled: false }
                : student;
            });
            console.log('수강생 제거 후 목록:', updatedStudents);
            return updatedStudents;
          });
          
          toast.success('수강생이 성공적으로 제거되었습니다.');
        }
      } else {
        // 새 학생 등록
        console.log('학생 등록 API 요청:', `classes/${classId}/students`, { studentId });
        const response = await authAxios.post(`/classes/${classId}/students`, { studentId });
        console.log('학생 등록 API 응답:', response);
        
        // API 호출이 성공한 경우에만 UI 업데이트
        if (response.status === 200 || response.status === 201) {
          // 전체 학생 목록 업데이트 (isEnrolled 플래그 변경)
          setClassStudents(prevStudents => {
            console.log('수강생 등록 전 목록:', prevStudents);
            const updatedStudents = prevStudents.map(student => {
              const studentIdMatches = student.id === studentId || student._id === studentId;
              return studentIdMatches
                ? { ...student, isEnrolled: true }
                : student;
            });
            console.log('수강생 등록 후 목록:', updatedStudents);
            return updatedStudents;
          });
          
          toast.success('수강생이 성공적으로 등록되었습니다.');
        }
      }
      
      // API 호출 성공 여부와 상관없이 강의 학생 목록 다시 불러오기
      fetchClassStudents(classId);
      
      // 전체 강의 및 구성 데이터 새로고침
      await Promise.all([
        fetchClasses(),
        fetchCourseStructures()
      ]);
      
      // 현재 강의와 관련된 프로그램 데이터 갱신
      const currentClassItem = classes.find(c => c.id === classId);
      if (currentClassItem && currentClassItem.moduleId) {
        const relatedPrograms = programs.filter(p => p.moduleId === currentClassItem.moduleId);
        for (const program of relatedPrograms) {
          preloadLinkedClasses(program.id);
        }
      }
      
      // 프로그램 연결 정보 초기화
      setProgramLinkedClasses({});
      
    } catch (err) {
      console.error('수강생 상태 변경 상세 오류:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        endpoint: isEnrolled ? `DELETE /classes/${classId}/students/${studentId}` : `POST /classes/${classId}/students`,
        stack: err.stack
      });
      toast.error(`수강생 ${isEnrolled ? '제거' : '등록'} 중 오류가 발생했습니다.`);
    } finally {
      setIsLoadingStudents(false);
    }
  };

  // 수강인원 관리 모달 렌더링
  const renderStudentManagementModal = () => {
    // 필터 적용된 대상 수강생 (캠퍼스와 카테고리로 필터링)
    const [filteredTargetStudents, setFilteredTargetStudents] = useState([]);
    
    // 필터링 조건 상태
    const [filterInfo, setFilterInfo] = useState({
      category: true,   // 카테고리 필터 적용 여부
      campus: true      // 캠퍼스 필터 적용 여부
    });
    
    // 필터 변경 핸들러
    const handleFilterChange = (filterType, checked) => {
      setFilterInfo(prev => ({
        ...prev,
        [filterType]: checked
      }));
    };
    
    // 필터 적용 효과
    useEffect(() => {
      if (!currentClass || !allStudents.length) {
        setFilteredTargetStudents([]);
        return;
      }
      
      // 현재 강의의 필터링 정보
      const classCategory = currentClass.category;
      const classroomCategory = classInfo.classroomCategory;
      
      console.log('필터링 조건:', {
        카테고리필터적용: filterInfo.category,
        캠퍼스필터적용: filterInfo.campus,
        강의카테고리: classCategory,
        강의실카테고리: classroomCategory
      });
      
      // 등록된 학생 ID 집합 생성
      const enrolledStudentIds = new Set(
        classStudents
          .filter(student => student.isEnrolled)
          .map(student => student.id)
      );
      
      console.log('등록된 학생 ID 목록:', Array.from(enrolledStudentIds));
      
      // 필터링 적용
      const filtered = allStudents.filter(student => {
        // 카테고리 필터링 (filterInfo.category가 true일 때만 적용)
        const categoryMatch = !filterInfo.category || 
                             !student.category || 
                             !classCategory || 
                             student.category === classCategory;
        
        // 캠퍼스 필터링 (filterInfo.campus가 true일 때만 적용)
        const campusMatch = !filterInfo.campus || 
                           !student.campus || 
                           !classroomCategory || 
                           student.campus === classroomCategory;
        
        // 두 조건을 모두 만족해야 함
        return categoryMatch && campusMatch;
      }).map(student => ({
        ...student,
        // 해당 학생이 등록되었는지 여부를 isEnrolled 속성으로 추가
        isEnrolled: enrolledStudentIds.has(student.id) || enrolledStudentIds.has(student._id)
      }));
      
      console.log(`필터링 결과: 전체 ${allStudents.length}명 중 ${filtered.length}명 표시`);
      console.log('필터링 교육생 목록:', filtered);
      setFilteredTargetStudents(filtered);
    }, [allStudents, currentClass, classInfo, filterInfo, classStudents]);

    return (
      <Modal
        title={`수강인원 관리 - ${currentClass?.name || ''}`}
        visible={showStudentManagement}
        onCancel={() => {
          setShowStudentManagement(false);
          setCurrentClass(null);
          setClassStudents([]);
        }}
        width={1200}
        footer={null}
      >
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium">수강생 목록</h3>
              <p className="text-sm text-gray-500">
                현재 {classStudents.filter(student => student.isEnrolled).length}명의 수강생이 등록되어 있습니다.
              </p>
            </div>
            <div className="text-right text-sm text-gray-500">
              <p>교육분류: <span className="font-medium">{classInfo.category}</span></p>
              <p>교육장소: <span className="font-medium">{classInfo.classroom}</span></p>
              <p>교육장 카테고리: <span className="font-medium">{classInfo.classroomCategory}</span></p>
            </div>
          </div>
          
          {/* 필터링 옵션 */}
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="text-sm font-medium mb-2">대상 수강생 필터링</div>
            <div className="flex items-center space-x-4">
              <Checkbox 
                checked={filterInfo.category} 
                onChange={(e) => handleFilterChange('category', e.target.checked)}
              >
                <span className="text-sm">교육분류 필터 ({classInfo.category})</span>
              </Checkbox>
              <Checkbox 
                checked={filterInfo.campus} 
                onChange={(e) => handleFilterChange('campus', e.target.checked)}
              >
                <span className="text-sm">교육장 카테고리 필터 ({classInfo.classroomCategory})</span>
              </Checkbox>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              필터링 결과: 전체 {allStudents.length}명 중 {filteredTargetStudents.length}명 표시
            </div>
          </div>

          <Tabs defaultActiveKey="all">
            <TabPane tab="전체" key="all">
              <div className="grid grid-cols-2 gap-4">
                {/* 대상 수강생 목록 - 필터링된 목록 사용 */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">대상 수강생</h4>
                                        <Table
                        dataSource={filteredTargetStudents}
                        rowKey="id"
                        columns={[
                          {
                            title: '기수',
                            dataIndex: 'batch',
                            key: 'batch',
                            render: (batch) => batch ? `${batch}기` : '-',
                          },
                          {
                            title: '이름',
                            dataIndex: 'name',
                            key: 'name',
                          },
                          {
                            title: '연락처',
                            dataIndex: 'phone',
                            key: 'phone',
                          },
                          {
                            title: '교육분류',
                            dataIndex: 'category',
                            key: 'category',
                            render: (category) => category || '-',
                            width: 90,
                          },
                          {
                            title: '교육장',
                            dataIndex: 'campus',
                            key: 'campus',
                            render: (campus) => campus || '-',
                            width: 90,
                          },
                          {
                            title: '관리',
                            key: 'action',
                            width: 80,
                            render: (_, record) => (
                              record.isEnrolled ? (
                                <Tag color="green">등록됨</Tag>
                              ) : (
                                <Button
                                  type="primary"
                                  size="small"
                                  onClick={() => toggleStudentEnrollment(currentClass.id, record.id, false)}
                                >
                                  추가
                                </Button>
                              )
                            ),
                          },
                        ]}
                  />
                </div>

                {/* 현재 강의 수강생 목록 */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3">현재 강의 수강생</h4>
                  <Table
                    dataSource={classStudents.filter(student => student.isEnrolled)}
                    rowKey="id"
                    columns={[
                      {
                        title: '기수',
                        dataIndex: 'batch',
                        key: 'batch',
                        render: (batch) => batch ? `${batch}기` : '-',
                      },
                      {
                        title: '이름',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: '연락처',
                        dataIndex: 'phone',
                        key: 'phone',
                      },
                      {
                        title: '교육분류',
                        dataIndex: 'category',
                        key: 'category',
                        render: (category) => category || '-',
                        width: 90,
                      },
                      {
                        title: '교육장',
                        dataIndex: 'campus',
                        key: 'campus',
                        render: (campus) => campus || '-',
                        width: 90,
                      },
                      {
                        title: '관리',
                        key: 'action',
                        width: 80,
                        render: (_, record) => (
                          <Button
                            danger
                            size="small"
                            onClick={() => {
                              if (window.confirm('정말 이 학생을 제거하시겠습니까?')) {
                                toggleStudentEnrollment(currentClass.id, record.id, true);
                              }
                            }}
                          >
                            제거
                          </Button>
                        ),
                      },
                    ]}
                  />
                </div>
              </div>
            </TabPane>
            {existingBatches.map(batch => (
              <TabPane tab={`${batch}기`} key={batch}>
                <div className="grid grid-cols-2 gap-4">
                  {/* 해당 기수 대상 수강생 목록 - 필터링된 목록 사용 */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">{batch}기 대상 수강생</h4>
                    <Table
                      dataSource={filteredTargetStudents.filter(student => student.batch?.toString() === batch)}
                      rowKey="id"
                      columns={[
                        {
                          title: '이름',
                          dataIndex: 'name',
                          key: 'name',
                        },
                        {
                          title: '연락처',
                          dataIndex: 'phone',
                          key: 'phone',
                        },
                        {
                          title: '교육분류',
                          dataIndex: 'category',
                          key: 'category',
                          render: (category) => category || '-',
                          width: 90,
                        },
                        {
                          title: '교육장',
                          dataIndex: 'campus',
                          key: 'campus',
                          render: (campus) => campus || '-',
                          width: 90,
                        },
                        {
                          title: '관리',
                          key: 'action',
                          width: 80,
                          render: (_, record) => (
                            record.isEnrolled ? (
                              <Tag color="green">등록됨</Tag>
                            ) : (
                              <Button
                                type="primary"
                                size="small"
                                onClick={() => toggleStudentEnrollment(currentClass.id, record.id, false)}
                              >
                                추가
                              </Button>
                            )
                          ),
                        },
                      ]}
                    />
                  </div>

                  {/* 현재 강의 수강생 목록 (기수별) */}
                  <div className="border rounded-lg p-4">
                    <h4 className="font-medium mb-3">{batch}기 현재 강의 수강생</h4>
                    <Table
                      dataSource={classStudents.filter(student => 
                        student.batch?.toString() === batch && student.isEnrolled
                      )}
                      rowKey="id"
                      columns={[
                        {
                          title: '이름',
                          dataIndex: 'name',
                          key: 'name',
                        },
                        {
                          title: '연락처',
                          dataIndex: 'phone',
                          key: 'phone',
                        },
                        {
                          title: '교육분류',
                          dataIndex: 'category',
                          key: 'category',
                          render: (category) => category || '-',
                          width: 90,
                        },
                        {
                          title: '교육장',
                          dataIndex: 'campus',
                          key: 'campus',
                          render: (campus) => campus || '-',
                          width: 90,
                        },
                        {
                          title: '관리',
                          key: 'action',
                          width: 80,
                          render: (_, record) => (
                            <Button
                              danger
                              size="small"
                              onClick={() => {
                                if (window.confirm('정말 이 학생을 제거하시겠습니까?')) {
                                  toggleStudentEnrollment(currentClass.id, record.id, true);
                                }
                              }}
                            >
                              제거
                            </Button>
                          ),
                        },
                      ]}
                    />
                  </div>
                </div>
              </TabPane>
            ))}
          </Tabs>
        </div>
      </Modal>
    );
  };

  // 프로그램-강의 연결 관련 상태 추가
  const [showClassLinkModal, setShowClassLinkModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedClasses, setSelectedClasses] = useState({
    shortTerm: [],
    mediumTerm: [],
    longTerm: []
  });
  // 강의 검색 및 필터 관련 상태 추가
  const [classSearchText, setClassSearchText] = useState('');
  const [currentClassCategory, setCurrentClassCategory] = useState('shortTerm');

  // 강의 연결 모달 열기
  const openClassLinkModal = (program, e) => {
    if (e) e.stopPropagation(); // 이벤트 전파 방지 추가
    setSelectedProgram(program);
    // 기존 연결된 강의 확인 로직 추가 (백엔드 API 필요)
    fetchLinkedClasses(program.id);
    setShowClassLinkModal(true);
  };

  // 프로그램에 연결된 강의 목록 가져오기
  const fetchLinkedClasses = async (programId) => {
    try {
      // 초기화
      setSelectedClasses({
        shortTerm: [],
        mediumTerm: [],
        longTerm: []
      });

      // 이미 연결된 강의 불러오기
      const response = await authAxios.get(`/classes/programs/${programId}/classes`);
      const { shortTermClasses, mediumTermClasses, longTermClasses } = response.data;
      
      // 강의 ID를 기반으로 실제 강의 객체 찾기
      const shortTermClassObjs = classes.filter(c => shortTermClasses.includes(c.id));
      const mediumTermClassObjs = classes.filter(c => mediumTermClasses.includes(c.id));
      const longTermClassObjs = classes.filter(c => longTermClasses.includes(c.id));
      
      // 선택된 강의 상태 업데이트
      setSelectedClasses({
        shortTerm: shortTermClassObjs,
        mediumTerm: mediumTermClassObjs,
        longTerm: longTermClassObjs
      });
    } catch (err) {
      console.error('연결된 강의 불러오기 오류:', err);
      toast.error('연결된 강의를 불러오는 중 오류가 발생했습니다.');
    }
  };

  // 특정 프로그램에 연결된 강의 목록 가져오기 (확장 가능한 하위 테이블용)
  const fetchLinkedClassesForProgram = async (programId) => {
    try {
      // 강의 목록 최신화
      await fetchClasses();
      
      // 이미 연결된 강의 불러오기
      const response = await authAxios.get(`/classes/programs/${programId}/classes`);
      const { shortTermClasses, mediumTermClasses, longTermClasses } = response.data;
      
      // 최신 강의 데이터 사용을 위해 현재 상태에서 가져옴
      const currentClasses = [...classes];
      
      // 강의 ID를 기반으로 실제 강의 객체 찾기
      const shortTermClassObjs = currentClasses.filter(c => 
        shortTermClasses.includes(c.id) || shortTermClasses.includes(c._id)
      );
      
      const mediumTermClassObjs = currentClasses.filter(c => 
        mediumTermClasses.includes(c.id) || mediumTermClasses.includes(c._id)
      );
      
      const longTermClassObjs = currentClasses.filter(c => 
        longTermClasses.includes(c.id) || longTermClasses.includes(c._id)
      );
      
      // 연결 정보 로그 출력
      console.log(`프로그램(${programId}) 연결 강의:`, {
        단기: shortTermClassObjs.length,
        중기: mediumTermClassObjs.length,
        장기: longTermClassObjs.length
      });
      
      return {
        shortTerm: shortTermClassObjs,
        mediumTerm: mediumTermClassObjs,
        longTerm: longTermClassObjs
      };
    } catch (err) {
      console.error('프로그램 연결 강의 조회 오류:', err);
      toast.error('강의 목록을 불러오는 중 오류가 발생했습니다.');
      return {
        shortTerm: [],
        mediumTerm: [],
        longTerm: []
      };
    }
  };

  // 강의 연결 저장
  const saveClassLink = async () => {
    try {
      if (!selectedProgram) return;

      // 각 카테고리별 시간 합계 계산
      const shortTermTotal = selectedClasses.shortTerm.reduce((sum, c) => sum + (c.hours || 0), 0);
      const mediumTermTotal = selectedClasses.mediumTerm.reduce((sum, c) => sum + (c.hours || 0), 0);
      const longTermTotal = selectedClasses.longTerm.reduce((sum, c) => sum + (c.hours || 0), 0);

      // API 호출하여 강의 연결
      await authAxios.put(`/classes/programs/${selectedProgram.id}/classes`, {
        shortTermClasses: selectedClasses.shortTerm.map(c => c.id),
        mediumTermClasses: selectedClasses.mediumTerm.map(c => c.id),
        longTermClasses: selectedClasses.longTerm.map(c => c.id)
      });

      // 전체 데이터 새로고침
      await Promise.all([
        fetchCourseStructures(),
        fetchClasses()
      ]);
      
      // 연결된 강의 데이터 새로고침
      preloadLinkedClasses(selectedProgram.id);

      toast.success('강의가 성공적으로 연결되었습니다.');
      setShowClassLinkModal(false);
    } catch (err) {
      console.error('강의 연결 오류:', err);
      toast.error('강의 연결 중 오류가 발생했습니다.');
    }
  };

  // 강의 검색 필터 함수
  const filterClasses = (category) => {
    const filteredByCategory = classes.filter(c => c.category === category);
    
    if (!classSearchText) {
      return filteredByCategory;
    }
    
    const searchLower = classSearchText.toLowerCase();
    return filteredByCategory.filter(c => 
      c.name.toLowerCase().includes(searchLower) ||
      (c.instructorName && c.instructorName.toLowerCase().includes(searchLower)) ||
      (c.module && c.module.toLowerCase().includes(searchLower))
    );
  };

  // 특정 프로그램에 대한 연결된 강의 데이터를 미리 로드
  const [programLinkedClasses, setProgramLinkedClasses] = useState({});

  // 프로그램 클릭 시 연결된 강의 데이터를 미리 로드하는 함수
  const preloadLinkedClasses = useCallback(async (programId) => {
    try {
      // 강제로 새로 로드하거나 기존 데이터가 없을 때 로드
      const result = await fetchLinkedClassesForProgram(programId);
      setProgramLinkedClasses(prev => ({
        ...prev,
        [programId]: result
      }));
    } catch (err) {
      console.error('연결된 강의 데이터 미리 로드 오류:', err);
    }
  }, [fetchLinkedClassesForProgram]);

  // 강의 연결 모달 렌더링
  const renderClassLinkModal = () => (
    <Modal
      title={`강의 연결 - ${selectedProgram?.name || ''}`}
      visible={showClassLinkModal}
      onCancel={(e) => {
        e.stopPropagation();
        setShowClassLinkModal(false);
      }}
      width={1000}
      footer={[
        <Button key="cancel" onClick={(e) => {
          e.stopPropagation();
          setShowClassLinkModal(false);
        }}>
          취소
        </Button>,
        <Button key="submit" type="primary" onClick={(e) => {
          e.stopPropagation();
          saveClassLink();
        }}>
          저장
        </Button>
      ]}
    >
      <div className="space-y-4">
        {/* 프로그램 정보 요약 */}
        <div className="bg-gray-50 p-4 rounded-md">
          <div className="grid grid-cols-4 gap-4">
            <div>
              <div className="text-sm text-gray-500">구성</div>
              <div className="font-medium">{selectedProgram?.structureType || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">단기 시간</div>
              <div className="font-medium">{selectedProgram?.shortTermHours || 0}시간</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">중기 시간</div>
              <div className="font-medium">{selectedProgram?.mediumTermHours || 0}시간</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">장기 시간</div>
              <div className="font-medium">{selectedProgram?.longTermHours || 0}시간</div>
            </div>
          </div>
        </div>

        {/* 카테고리 탭 */}
        <Tabs 
          activeKey={currentClassCategory} 
          onChange={setCurrentClassCategory}
        >
          <TabPane tab={`단기 강의 (${selectedProgram?.shortTermHours || 0}시간)`} key="shortTerm">
            <div className="mb-3">
              <input
                type="text"
                placeholder="강의명, 강사명 등으로 검색..."
                value={classSearchText}
                onChange={(e) => setClassSearchText(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm">선택된 시간: {selectedClasses.shortTerm.reduce((sum, c) => sum + (c.hours || 0), 0)}시간</div>
              {selectedClasses.shortTerm.length > 0 && (
                <Button 
                  size="small" 
                  danger 
                  onClick={() => setSelectedClasses(prev => ({...prev, shortTerm: []}))}>
                  모두 해제
                </Button>
              )}
            </div>
            <Table
              dataSource={filterClasses('단기')}
              rowKey="id"
              size="small"
              columns={[
                {
                  title: '강의명',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record) => (
                    <div className={selectedClasses.shortTerm.some(c => c.id === record.id) ? 'font-medium text-blue-600' : ''}>
                      {text}
                    </div>
                  )
                },
                {
                  title: '모듈',
                  dataIndex: 'module',
                  key: 'module',
                },
                {
                  title: '강사',
                  dataIndex: 'instructorName',
                  key: 'instructorName',
                },
                {
                  title: '날짜',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => new Date(date).toLocaleDateString()
                },
                {
                  title: '시간',
                  dataIndex: 'hours',
                  key: 'hours',
                  width: 80,
                  align: 'center'
                },
                {
                  title: '선택',
                  key: 'action',
                  width: 80,
                  align: 'center',
                  render: (_, record) => (
                    <Checkbox
                      checked={selectedClasses.shortTerm.some(c => c.id === record.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClasses(prev => ({
                            ...prev,
                            shortTerm: [...prev.shortTerm, record]
                          }));
                        } else {
                          setSelectedClasses(prev => ({
                            ...prev,
                            shortTerm: prev.shortTerm.filter(c => c.id !== record.id)
                          }));
                        }
                      }}
                    />
                  )
                }
              ]}
            />
          </TabPane>

          <TabPane tab={`중기 강의 (${selectedProgram?.mediumTermHours || 0}시간)`} key="mediumTerm">
            <div className="mb-3">
              <input
                type="text"
                placeholder="강의명, 강사명 등으로 검색..."
                value={classSearchText}
                onChange={(e) => setClassSearchText(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm">선택된 시간: {selectedClasses.mediumTerm.reduce((sum, c) => sum + (c.hours || 0), 0)}시간</div>
              {selectedClasses.mediumTerm.length > 0 && (
                <Button 
                  size="small" 
                  danger 
                  onClick={() => setSelectedClasses(prev => ({...prev, mediumTerm: []}))}>
                  모두 해제
                </Button>
              )}
            </div>
            <Table
              dataSource={filterClasses('중기')}
              rowKey="id"
              size="small"
              columns={[
                {
                  title: '강의명',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record) => (
                    <div className={selectedClasses.mediumTerm.some(c => c.id === record.id) ? 'font-medium text-blue-600' : ''}>
                      {text}
                    </div>
                  )
                },
                {
                  title: '모듈',
                  dataIndex: 'module',
                  key: 'module',
                },
                {
                  title: '강사',
                  dataIndex: 'instructorName',
                  key: 'instructorName',
                },
                {
                  title: '날짜',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => new Date(date).toLocaleDateString()
                },
                {
                  title: '시간',
                  dataIndex: 'hours',
                  key: 'hours',
                  width: 80,
                  align: 'center'
                },
                {
                  title: '선택',
                  key: 'action',
                  width: 80,
                  align: 'center',
                  render: (_, record) => (
                    <Checkbox
                      checked={selectedClasses.mediumTerm.some(c => c.id === record.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClasses(prev => ({
                            ...prev,
                            mediumTerm: [...prev.mediumTerm, record]
                          }));
                        } else {
                          setSelectedClasses(prev => ({
                            ...prev,
                            mediumTerm: prev.mediumTerm.filter(c => c.id !== record.id)
                          }));
                        }
                      }}
                    />
                  )
                }
              ]}
            />
          </TabPane>

          <TabPane tab={`장기 강의 (${selectedProgram?.longTermHours || 0}시간)`} key="longTerm">
            <div className="mb-3">
              <input
                type="text"
                placeholder="강의명, 강사명 등으로 검색..."
                value={classSearchText}
                onChange={(e) => setClassSearchText(e.target.value)}
                className="w-full border rounded-md p-2"
              />
            </div>
            <div className="flex justify-between items-center mb-2">
              <div className="text-sm">선택된 시간: {selectedClasses.longTerm.reduce((sum, c) => sum + (c.hours || 0), 0)}시간</div>
              {selectedClasses.longTerm.length > 0 && (
                <Button 
                  size="small" 
                  danger 
                  onClick={() => setSelectedClasses(prev => ({...prev, longTerm: []}))}>
                  모두 해제
                </Button>
              )}
            </div>
            <Table
              dataSource={filterClasses('장기')}
              rowKey="id"
              size="small"
              columns={[
                {
                  title: '강의명',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record) => (
                    <div className={selectedClasses.longTerm.some(c => c.id === record.id) ? 'font-medium text-blue-600' : ''}>
                      {text}
                    </div>
                  )
                },
                {
                  title: '모듈',
                  dataIndex: 'module',
                  key: 'module',
                },
                {
                  title: '강사',
                  dataIndex: 'instructorName',
                  key: 'instructorName',
                },
                {
                  title: '날짜',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => new Date(date).toLocaleDateString()
                },
                {
                  title: '시간',
                  dataIndex: 'hours',
                  key: 'hours',
                  width: 80,
                  align: 'center'
                },
                {
                  title: '선택',
                  key: 'action',
                  width: 80,
                  align: 'center',
                  render: (_, record) => (
                    <Checkbox
                      checked={selectedClasses.longTerm.some(c => c.id === record.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedClasses(prev => ({
                            ...prev,
                            longTerm: [...prev.longTerm, record]
                          }));
                        } else {
                          setSelectedClasses(prev => ({
                            ...prev,
                            longTerm: prev.longTerm.filter(c => c.id !== record.id)
                          }));
                        }
                      }}
                    />
                  )
                }
              ]}
            />
          </TabPane>
        </Tabs>
      </div>
    </Modal>
  );

  // 프로그램에 새 강의 추가 관련 상태 추가
  const [showAddClassToProgramModal, setShowAddClassToProgramModal] = useState(false);
  const [selectedProgramForNewClass, setSelectedProgramForNewClass] = useState(null);
  const [newClassCategory, setNewClassCategory] = useState('단기');

  // 프로그램에 새 강의 추가 모달 열기
  const openAddClassToProgramModal = (program, e) => {
    if (e) e.stopPropagation(); // 이벤트 전파 방지 추가
    setSelectedProgramForNewClass(program);
    setNewClassCategory('단기'); // 기본값으로 단기 설정
    reset(); // 폼 초기화
    setShowAddClassToProgramModal(true);
  };

  // 프로그램에 새 강의 추가 처리
  const handleAddClassToProgram = async (data) => {
    try {
      setIsSubmitting(true);
      console.log('프로그램에 새 강의 추가 시작:', data.name);
      
      // 현재 사용자 정보
      const currentUserId = user?._id || user?.id;
      const currentUserName = user?.name || user?.username || '강사';
      
      // 날짜 포맷팅
      const classDate = new Date(data.date);
      
      // 시작 시간과 종료 시간을 Date 객체로 변환 (필수가 아닌 경우 처리)
      let startTimeDate = null;
      let endTimeDate = null;
      
      if (data.startTime) {
        const [startHour, startMinute] = data.startTime.split(':').map(Number);
        startTimeDate = new Date(classDate);
        startTimeDate.setHours(startHour, startMinute, 0);
      }
      
      if (data.endTime) {
        const [endHour, endMinute] = data.endTime.split(':').map(Number);
        endTimeDate = new Date(classDate);
        endTimeDate.setHours(endHour, endMinute, 0);
      }

      // 선택된 프로그램 정보로부터 모듈 정보 가져오기
      const moduleInfo = {
        moduleId: selectedProgramForNewClass.moduleId,
        moduleName: modules.find(m => m.id === selectedProgramForNewClass.moduleId)?.name || '',
        structureType: selectedProgramForNewClass.structureType,
        programs: [{ 
          programId: selectedProgramForNewClass.id, 
          programName: selectedProgramForNewClass.name 
        }]
      };

      // API에 전송할 데이터 구성
      const classData = {
        name: data.name,
        instructorId: data.instructorId || currentUserId,
        instructorName: data.instructorName || currentUserName,
        date: classDate.toISOString(),
        startTime: startTimeDate ? startTimeDate.toISOString() : null,
        endTime: endTimeDate ? endTimeDate.toISOString() : null,
        description: data.description || '',
        room: data.room || '',
        classroomId: data.classroomId || null,
        category: newClassCategory, // 선택된 카테고리 사용
        structure: selectedProgramForNewClass.structureType || '공통',
        module: moduleInfo.moduleName || '',
        moduleId: moduleInfo.moduleId,
        program: selectedProgramForNewClass.name,
        programId: selectedProgramForNewClass.id,
        moduleInfo: moduleInfo
      };
      
      console.log('새 강의 데이터:', classData);
      
      // API 호출
      const response = await authAxios.post('/classes', classData);
      console.log('강의 생성 응답:', response.data);
      
      // 새로 생성된 강의를 프로그램에 자동 연결
      const newClassId = response.data.id || response.data._id;
      console.log('새 강의 ID:', newClassId);
      
      // 강의 카테고리에 따라 연결 데이터 구성
      let shortTermClasses = [];
      let mediumTermClasses = [];
      let longTermClasses = [];
      
      // 기존 연결 데이터 가져오기
      try {
        console.log('기존 프로그램 연결 데이터 조회');
        const linkResponse = await authAxios.get(`/classes/programs/${selectedProgramForNewClass.id}/classes`);
        shortTermClasses = linkResponse.data.shortTermClasses || [];
        mediumTermClasses = linkResponse.data.mediumTermClasses || [];
        longTermClasses = linkResponse.data.longTermClasses || [];
        console.log('기존 연결 데이터:', {
          단기: shortTermClasses.length,
          중기: mediumTermClasses.length,
          장기: longTermClasses.length
        });
      } catch (err) {
        console.error('기존 연결 데이터 조회 오류:', err);
      }
      
      // 새 강의 ID를 해당 카테고리에 추가
      if (newClassCategory === '단기') {
        shortTermClasses.push(newClassId);
      } else if (newClassCategory === '중기') {
        mediumTermClasses.push(newClassId);
      } else if (newClassCategory === '장기') {
        longTermClasses.push(newClassId);
      }
      
      console.log('강의 연결 요청 데이터:', {
        단기: shortTermClasses.length,
        중기: mediumTermClasses.length,
        장기: longTermClasses.length
      });
      
      // 강의 연결 API 호출
      await authAxios.put(`/classes/programs/${selectedProgramForNewClass.id}/classes`, {
        shortTermClasses,
        mediumTermClasses,
        longTermClasses
      });
      
      // 직접 클래스 상태 업데이트 - 서버에서 받은 데이터 확인
      if (response.data && newClassId) {
        console.log('새 강의 정보를 상태에 직접 추가');
        setClasses(prevClasses => {
          // 이미 있는지 확인
          const exists = prevClasses.some(c => c.id === newClassId || c._id === newClassId);
          if (exists) {
            console.log('이미 강의가 상태에 존재함');
            return prevClasses;
          }
          
          // 없으면 새로 추가
          const normalizedClass = {
            ...response.data,
            id: newClassId
          };
          
          return [...prevClasses, normalizedClass];
        });
      }
      
      // 전체 데이터 새로고침
      console.log('데이터 새로고침 시작');
      const [newClasses, newStructures] = await Promise.all([
        fetchClasses(),
        fetchCourseStructures()
      ]);
      console.log('데이터 새로고침 완료:', {
        강의목록: newClasses.length,
        구성데이터: newStructures ? '업데이트됨' : '오류'
      });
      
      // 프로그램 연결 정보 갱신
      console.log('프로그램 연결 정보 갱신');
      await preloadLinkedClasses(selectedProgramForNewClass.id);
      
      // 모듈 관련 다른 프로그램들의 연결 정보도 갱신
      if (selectedProgramForNewClass.moduleId) {
        console.log('관련 프로그램 연결 정보 갱신');
        const relatedPrograms = programs.filter(p => 
          p.moduleId === selectedProgramForNewClass.moduleId && 
          p.id !== selectedProgramForNewClass.id
        );
        
        for (const program of relatedPrograms) {
          await preloadLinkedClasses(program.id);
        }
      }
      
      setShowAddClassToProgramModal(false);
      toast.success('새 강의가 프로그램에 추가 및 연결되었습니다.');
      reset();
      
      // 강제 리렌더링을 위한 타이머 설정
      setTimeout(() => {
        console.log('등록 후 추가 새로고침');
        fetchClasses();
        
        // 연결 정보 캐시 초기화 및 재로드
        setProgramLinkedClasses({});
        preloadLinkedClasses(selectedProgramForNewClass.id);
      }, 500);
      
    } catch (err) {
      console.error('강의 추가 오류:', err);
      toast.error('강의 추가 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
      setSelectedProgramForNewClass(null);
    }
  };

  // 프로그램에 새 강의 추가 모달 렌더링
  const renderAddClassToProgramModal = () => (
    <Modal
      title={`새 강의 추가 - ${selectedProgramForNewClass?.name || ''}`}
      visible={showAddClassToProgramModal}
      onCancel={(e) => {
        e.stopPropagation();
        setShowAddClassToProgramModal(false);
      }}
      footer={null}
      width={800}
    >
      <form onSubmit={handleSubmit(handleAddClassToProgram)} className="space-y-4">
        <div className="bg-gray-50 p-4 rounded-md mb-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-500">프로그램</div>
              <div className="font-medium">{selectedProgramForNewClass?.name || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">구성</div>
              <div className="font-medium">{selectedProgramForNewClass?.structureType || '-'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">카테고리</div>
              <div>
                <select
                  value={newClassCategory}
                  onChange={(e) => setNewClassCategory(e.target.value)}
                  className="border rounded-md p-1"
                >
                  <option value="단기">단기</option>
                  <option value="중기">중기</option>
                  <option value="장기">장기</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 강의명 */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              강의명 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              {...register('name', { required: '강의명은 필수입니다.' })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>
          
          {/* 강사 선택 */}
          <div>
            <label htmlFor="instructorId" className="block text-sm font-medium text-gray-700">
              담당 강사
            </label>
            <select
              id="instructorId"
              {...register('instructorId')}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="">-- 강사 선택 --</option>
              {instructors.map(instructor => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* 강의 날짜 */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700">
              강의 날짜 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              id="date"
              defaultValue={new Date().toISOString().split('T')[0]}
              {...register('date', { required: '강의 날짜는 필수입니다.' })}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
            {errors.date && (
              <p className="mt-1 text-sm text-red-600">{errors.date.message}</p>
            )}
          </div>
          
          {/* 강의실 - 드롭다운으로 변경 */}
          <div>
            <label htmlFor="classroomId" className="block text-sm font-medium text-gray-700">
              강의실
            </label>
            <select
              id="classroomId"
              {...register('classroomId')}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            >
              <option value="">-- 강의실 선택 --</option>
              {classrooms.map(classroom => (
                <option key={classroom.id || classroom._id} value={classroom.id || classroom._id}>
                  {classroom.name}
                </option>
              ))}
            </select>
          </div>
          
          {/* 시작 시간 */}
          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700">
              시작 시간 (선택)
            </label>
            <input
              type="time"
              id="startTime"
              {...register('startTime')}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          
          {/* 종료 시간 */}
          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-700">
              종료 시간 (선택)
            </label>
            <input
              type="time"
              id="endTime"
              {...register('endTime')}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
          
          {/* 설명 */}
          <div className="md:col-span-2">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              강의 설명
            </label>
            <textarea
              id="description"
              {...register('description')}
              rows="3"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
            />
          </div>
        </div>
        
        <div className="pt-4 flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => setShowAddClassToProgramModal(false)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center space-x-1"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>저장 중...</span>
              </>
            ) : (
              <span>저장</span>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );

  // 인라인 편집 관련 상태 추가
  const [editingLinkedClass, setEditingLinkedClass] = useState(null);
  const [editingLinkedClassField, setEditingLinkedClassField] = useState(null);
  const [editingLinkedClassValue, setEditingLinkedClassValue] = useState('');

  // 인라인 편집 시작 함수
  const startLinkedClassEdit = (classId, field, value) => {
    setEditingLinkedClass(classId);
    setEditingLinkedClassField(field);
    setEditingLinkedClassValue(value || '');
  };

  // 인라인 편집 저장 함수
  const saveLinkedClassEdit = async () => {
    if (!editingLinkedClass || !editingLinkedClassField) return;

    try {
      // 현재 편집 중인 클래스 찾기
      const classToUpdate = classes.find(c => c.id === editingLinkedClass);
      if (!classToUpdate) return;

      // API 호출을 위한 데이터 준비
      let updateData = {};

      // 필드에 따라 다른 처리
      if (editingLinkedClassField === 'instructorId') {
        const selectedInstructor = instructors.find(i => i.id === editingLinkedClassValue);
        updateData = {
          instructorId: editingLinkedClassValue,
          instructorName: selectedInstructor?.name || classToUpdate.instructorName
        };
      } else if (editingLinkedClassField === 'classroomId') {
        updateData = {
          classroomId: editingLinkedClassValue
        };
      } else {
        updateData = {
          [editingLinkedClassField]: editingLinkedClassValue
        };
      }

      // API 호출
      await authAxios.put(`/classes/${editingLinkedClass}`, updateData);

      // 전체 데이터 새로고침
      await Promise.all([
        fetchClasses(),
        fetchCourseStructures()
      ]);
      
      // 해당 강의와 관련된 프로그램 데이터 갱신
      if (classToUpdate.moduleId) {
        const relatedPrograms = programs.filter(p => p.moduleId === classToUpdate.moduleId);
        for (const program of relatedPrograms) {
          preloadLinkedClasses(program.id);
        }
      }
      
      // 프로그램 연결 정보도 갱신
      setProgramLinkedClasses({});
      
      toast.success('강의 정보가 업데이트되었습니다.');
    } catch (err) {
      console.error('인라인 편집 저장 오류:', err);
      toast.error('정보 업데이트 중 오류가 발생했습니다.');
    } finally {
      // 상태 초기화
      setEditingLinkedClass(null);
      setEditingLinkedClassField(null);
      setEditingLinkedClassValue('');
    }
  };

  // 인라인 편집 취소 함수
  const cancelLinkedClassEdit = () => {
    setEditingLinkedClass(null);
    setEditingLinkedClassField(null);
    setEditingLinkedClassValue('');
  };

  // Enter 키 핸들러 추가
  const handleLinkedClassEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveLinkedClassEdit();
    } else if (e.key === 'Escape') {
      cancelLinkedClassEdit();
    }
  };

  // LinkedClassesExpanded 컴포넌트 수정 (인라인 편집 기능 추가)
  const LinkedClassesExpanded = ({ program }) => {
    // programLinkedClasses에서 데이터를 가져오거나 빈 객체 반환
    const linkedClasses = programLinkedClasses[program.id] || {
      shortTerm: [],
      mediumTerm: [],
      longTerm: []
    };
    
    // 로딩 상태 확인
    const isLoading = !programLinkedClasses[program.id];
    
    // 컴포넌트 마운트 시 데이터 로드
    useEffect(() => {
      if (!programLinkedClasses[program.id]) {
        preloadLinkedClasses(program.id);
      }
    }, [program.id, preloadLinkedClasses, programLinkedClasses]);
    
    if (isLoading) {
      return (
        <div className="p-4 text-center">
          <Spin />
          <div className="mt-2 text-sm text-gray-500">연결된 강의 목록을 불러오는 중입니다...</div>
        </div>
      );
    }
    
    // 수강생 관리 모달을 열기 위한 핸들러
    const handleStudentManagement = (record, e) => {
      // 이벤트 전파 중지
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      console.log('수강인원 관리 모달 열기: ', record);
      setCurrentClass(record);
      setShowStudentManagement(true);
      
      // 강의 ID 확인 후 수강생 목록 로드
      const classId = record.id || record._id;
      if (classId) {
        console.log('수강인원 목록 조회 시작:', classId);
        setTimeout(() => {
          fetchClassStudents(classId);
        }, 100); // 약간 지연 시켜 모달이 먼저 열리도록 함
      } else {
        console.error('유효하지 않은 강의 ID:', record);
      }
    };

    // 강의 삭제 핸들러
    const handleDeleteClass = (record, e) => {
      // 이벤트 전파 중지
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (window.confirm('정말 이 강의를 삭제하시겠습니까?')) {
        deleteClass(record.id).then(() => {
          // 삭제 후 해당 프로그램의 연결된 강의 목록 다시 로드
          preloadLinkedClasses(program.id);
        });
      }
    };
    
    // 모달 열기 처리 함수 - 이벤트 전파 방지 기능 강화
    const handleOpenClassLinkModal = (e) => {
      // 이벤트 전파 중지
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      openClassLinkModal(program, e);
    };
    
    // 새 강의 추가 모달 열기 처리 함수 - 이벤트 전파 방지 기능 강화
    const handleOpenAddClassToProgramModal = (e) => {
      // 이벤트 전파 중지
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      openAddClassToProgramModal(program, e);
    };

    return (
      <div className="p-2 bg-gray-50 rounded" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex justify-between items-center">
          <h4 className="text-sm font-medium text-gray-700">연결된 강의 목록</h4>
          <div className="space-x-2">
            <Button 
              type="primary" 
              size="small" 
              onClick={handleOpenClassLinkModal}
              icon={<SwapOutlined />}
            >
              강의 관리
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleOpenAddClassToProgramModal}
              icon={<PlusOutlined />}
            >
              새 강의 추가
            </Button>
          </div>
        </div>
        
        {/* 모든 강의를 하나의 테이블로 통합 */}
        {(linkedClasses.shortTerm.length > 0 || 
          linkedClasses.mediumTerm.length > 0 || 
          linkedClasses.longTerm.length > 0) ? (
          <div className="mb-4">
            <Table
              dataSource={[
                ...linkedClasses.shortTerm.map(cls => ({...cls, category: '단기'})),
                ...linkedClasses.mediumTerm.map(cls => ({...cls, category: '중기'})),
                ...linkedClasses.longTerm.map(cls => ({...cls, category: '장기'}))
              ]}
              columns={[
                {
                  title: '유형',
                  dataIndex: 'category',
                  key: 'category',
                  width: 80,
                  render: (category) => {
                    let color = '';
                    if (category === '단기') color = 'blue';
                    else if (category === '중기') color = 'orange';
                    else if (category === '장기') color = 'green';
                    
                    return (
                      <Tag color={color}>{category}</Tag>
                    );
                  }
                },
                {
                  title: '강의명',
                  dataIndex: 'name',
                  key: 'name',
                  render: (text, record) => (
                    editingLinkedClass === record.id && editingLinkedClassField === 'name' ? (
                      <div className="flex items-center">
                        <input
                          type="text"
                          value={editingLinkedClassValue}
                          onChange={(e) => setEditingLinkedClassValue(e.target.value)}
                          onKeyDown={handleLinkedClassEditKeyDown}
                          onBlur={saveLinkedClassEdit}
                          className="border rounded px-2 py-1 mr-1 flex-grow"
                          autoFocus
                        />
                        <SaveOutlined 
                          className="text-green-500 cursor-pointer mx-1" 
                          onClick={saveLinkedClassEdit}
                        />
                        <CloseOutlined 
                          className="text-red-500 cursor-pointer" 
                          onClick={cancelLinkedClassEdit}
                        />
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer hover:text-blue-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          startLinkedClassEdit(record.id, 'name', text);
                        }}
                      >
                        {text}
                      </div>
                    )
                  )
                },
                {
                  title: '강사',
                  dataIndex: 'instructorId',
                  key: 'instructorId',
                  render: (instructorId, record) => {
                    const instructor = instructors.find(i => i.id === instructorId || i._id === instructorId);
                    return (
                      editingLinkedClass === record.id && editingLinkedClassField === 'instructorId' ? (
                        <div className="flex items-center">
                          <select
                            value={editingLinkedClassValue}
                            onChange={(e) => setEditingLinkedClassValue(e.target.value)}
                            onBlur={saveLinkedClassEdit}
                            className="border rounded px-2 py-1 mr-1 flex-grow"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          >
                            <option value="">-- 강사 선택 --</option>
                            {instructors.map(instructor => (
                              <option key={instructor.id} value={instructor.id}>
                                {instructor.name}
                              </option>
                            ))}
                          </select>
                          <SaveOutlined 
                            className="text-green-500 cursor-pointer mx-1" 
                            onClick={(e) => {
                              e.stopPropagation();
                              saveLinkedClassEdit();
                            }}
                          />
                          <CloseOutlined 
                            className="text-red-500 cursor-pointer" 
                            onClick={(e) => {
                              e.stopPropagation();
                              cancelLinkedClassEdit();
                            }}
                          />
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer hover:text-blue-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            startLinkedClassEdit(record.id, 'instructorId', instructorId);
                          }}
                        >
                          {instructor?.name || record.instructorName || '-'}
                        </div>
                      )
                    );
                  }
                },
                {
                  title: '날짜',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date, record) => (
                    editingLinkedClass === record.id && editingLinkedClassField === 'date' ? (
                      <div className="flex items-center">
                        <input
                          type="date"
                          value={editingLinkedClassValue}
                          onChange={(e) => setEditingLinkedClassValue(e.target.value)}
                          onKeyDown={handleLinkedClassEditKeyDown}
                          onBlur={saveLinkedClassEdit}
                          className="border rounded px-2 py-1 mr-1 flex-grow"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                        <SaveOutlined 
                          className="text-green-500 cursor-pointer mx-1" 
                          onClick={(e) => {
                            e.stopPropagation();
                            saveLinkedClassEdit();
                          }}
                        />
                        <CloseOutlined 
                          className="text-red-500 cursor-pointer" 
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelLinkedClassEdit();
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="cursor-pointer hover:text-blue-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          const formattedDate = date ? new Date(date).toISOString().split('T')[0] : '';
                          startLinkedClassEdit(record.id, 'date', formattedDate);
                        }}
                      >
                        {date ? new Date(date).toLocaleDateString() : '-'}
                      </div>
                    )
                  )
                },
                {
                  title: '강의실',
                  dataIndex: 'classroomId',
                  key: 'classroomId',
                  render: (classroomId, record) => {
                    const classroom = classrooms.find(c => c._id === classroomId || c.id === classroomId);
                    return (
                      editingLinkedClass === record.id && editingLinkedClassField === 'classroomId' ? (
                        <div className="flex items-center">
                          <select
                            value={editingLinkedClassValue}
                            onChange={(e) => setEditingLinkedClassValue(e.target.value)}
                            onBlur={saveLinkedClassEdit}
                            className="border rounded px-2 py-1 mr-1 flex-grow"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          >
                            <option value="">-- 강의실 선택 --</option>
                            {classrooms.map(classroom => (
                              <option key={classroom._id || classroom.id} value={classroom._id || classroom.id}>
                                {classroom.name}
                              </option>
                            ))}
                          </select>
                          <SaveOutlined 
                            className="text-green-500 cursor-pointer mx-1" 
                            onClick={saveLinkedClassEdit}
                          />
                          <CloseOutlined 
                            className="text-red-500 cursor-pointer" 
                            onClick={cancelLinkedClassEdit}
                          />
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer hover:text-blue-500"
                          onClick={() => startLinkedClassEdit(record.id, 'classroomId', classroomId)}
                        >
                          {classroom?.name || '-'}
                        </div>
                      )
                    );
                  }
                },
                {
                  title: '관리',
                  key: 'action',
                  width: 120,
                  render: (_, record) => (
                    <div className="flex space-x-1" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="강의 삭제">
                        <Button
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          onClick={(e) => handleDeleteClass(record, e)}
                        />
                      </Tooltip>
                      <Tooltip title="수강인원 관리">
                        <Button
                          size="small"
                          icon={<TeamOutlined />}
                          onClick={(e) => handleStudentManagement(record, e)}
                        />
                      </Tooltip>
                    </div>
                  )
                }
              ]}
              size="small"
              pagination={false}
              rowKey="id"
              summary={() => (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={6}>
                    <div className="flex justify-between text-sm">
                      <span>단기: {linkedClasses.shortTerm.length}개</span>
                      <span>중기: {linkedClasses.mediumTerm.length}개</span>
                      <span>장기: {linkedClasses.longTerm.length}개</span>
                      <span>총 강의: {linkedClasses.shortTerm.length + linkedClasses.mediumTerm.length + linkedClasses.longTerm.length}개</span>
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </div>
        ) : (
          <div className="text-center p-4">
            <div className="text-gray-500">이 프로그램에 연결된 강의가 없습니다.</div>
            <div className="mt-2 space-x-2">
              <Button 
                type="link" 
                onClick={handleOpenClassLinkModal}
              >
                강의 추가/관리
              </Button>
              <Button
                type="link"
                onClick={handleOpenAddClassToProgramModal}
              >
                새 강의 추가하기
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // 구성 탭 변경 처리
  const handleStructureTabChange = (tabKey) => {
    setActiveStructureTab(tabKey);
    // 로컬 스토리지에 현재 구성 탭 저장
    localStorage.setItem('integratedClassManagement_structureTab', tabKey);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">강의 구성 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          강의 구성 정보를 효율적으로 관리하세요.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between mb-6">
          <div className="flex space-x-3">
            <Button 
              onClick={() => fetchCourseStructures()} 
              icon={<SyncOutlined />}
            >
              데이터 새로고침
            </Button>
          </div>
          <Button 
            type="dashed" 
            icon={<PlusOutlined />}
            onClick={() => setShowStructureForm(true)}
          >
            구성 추가
          </Button>
        </div>
        
        {/* 구성 탭 */}
        <Tabs 
          activeKey={activeStructureTab} 
          onChange={handleStructureTabChange}
          type="card"
        >
          {courseStructures.map(structure => (
            <TabPane 
              tab={
                <span>
                  {structure.name}
                  <Button 
                    type="text" 
                    size="small" 
                    danger
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      if(window.confirm(`'${structure.name}' 구성을 삭제하시겠습니까?`)) {
                        deleteStructure(structure.id);
                      }
                    }}
                  />
                </span>
              } 
              key={structure.name}
            >
              <div className="flex justify-between mb-4">
                <h3 className="text-lg font-medium">{structure.name} 모듈</h3>
                <Button 
                  onClick={() => {
                    setSelectedStructure(structure);
                    setNewModule({
                      ...newModule,
                      structureType: structure.name,
                      order: getModulesByStructure(structure.name).length + 1
                    });
                    setShowModuleForm(true);
                  }} 
                  icon={<PlusOutlined />}
                >
                  모듈 추가
                </Button>
              </div>
              
              <div className="space-y-6">
                {getModulesByStructure(structure.name).map(module => (
                  <Card 
                    key={module.id} 
                    title={
                      <div className="flex justify-between items-center">
                        <span>{module.name}</span>
                        <div className="flex space-x-2">
                          <Button 
                            size="small" 
                            onClick={() => {
                              setSelectedModule(module);
                              setNewProgram({
                                ...newProgram,
                                moduleId: module.id,
                                structureType: module.structureType,
                                order: getProgramsByModule(module.id).length + 1
                              });
                              setShowProgramForm(true);
                            }}
                            icon={<PlusOutlined />}
                          >
                            프로그램 추가
                          </Button>
                          <Button 
                            size="small" 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => {
                              if(window.confirm(`'${module.name}' 모듈을 삭제하시겠습니까?`)) {
                                deleteModule(module.id);
                              }
                            }}
                          />
                        </div>
                      </div>
                    }
                  >
                    <Table
                      dataSource={getProgramsByModule(module.id)}
                      rowKey="id"
                      size="small"
                      pagination={false}
                      defaultExpandAllRows={true}
                      expandable={{
                        expandedRowRender: record => {
                          // LinkedClassesExpanded 컴포넌트 직접 사용
                          return <LinkedClassesExpanded program={record} />;
                        },
                        expandIcon: ({ expanded, onExpand, record }) => (
                          <Button 
                            type="link" 
                            icon={expanded ? <CloseOutlined /> : <TeamOutlined />}
                            onClick={e => {
                              e.stopPropagation(); // 이벤트 전파 방지 추가
                              onExpand(record, e);
                            }}
                            size="small"
                          >
                            {/* {expanded ? '강의 목록 숨기기' : '강의 목록 보기'} */}
                          </Button>
                        ),
                      }}
                      columns={[
                        {
                          title: '프로그램',
                          dataIndex: 'name',
                          key: 'name',
                          render: (text, record) => (
                            <div className={record.isFromClass ? "text-blue-600 font-medium" : ""}>
                              {text}
                              {record.isFromClass && (
                                <Tooltip title="강의 관리에서 가져온 데이터">
                                  <span className="ml-2 text-xs text-blue-500">
                                    (강의)
                                  </span>
                                </Tooltip>
                              )}
                            </div>
                          )
                        },
                        {
                          title: '단기(시간)',
                          dataIndex: 'shortTermHours',
                          key: 'shortTermHours',
                          width: 100,
                          render: (text, record) => (
                            editingProgram?.id === record.id && editingProgramField === 'shortTermHours' ? (
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  value={editingProgramValue}
                                  onChange={(e) => setEditingProgramValue(parseInt(e.target.value) || 0)}
                                  onKeyDown={(e) => handleProgramEditKeyDown(e, record.id, 'shortTermHours', editingProgramValue)}
                                  onBlur={() => saveEditProgram(record.id, 'shortTermHours', editingProgramValue)}
                                  className="w-20 border rounded px-2 py-1 mr-1"
                                  min="0"
                                  autoFocus
                                />
                                <SaveOutlined 
                                  className="text-green-500 cursor-pointer mx-1" 
                                  onClick={() => saveEditProgram(record.id, 'shortTermHours', editingProgramValue)}
                                />
                                <CloseOutlined 
                                  className="text-red-500 cursor-pointer" 
                                  onClick={() => {
                                    setEditingProgram(null);
                                    setEditingProgramField(null);
                                    setEditingProgramValue(null);
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className={`cursor-pointer hover:bg-gray-100 text-center ${record.isFromClass ? "text-blue-600" : ""}`}
                                onClick={() => startEditProgram(record, 'shortTermHours')}
                              >
                                {text}
                              </div>
                            )
                          )
                        },
                        {
                          title: '중기(시간)',
                          dataIndex: 'mediumTermHours',
                          key: 'mediumTermHours',
                          width: 100,
                          render: (text, record) => (
                            editingProgram?.id === record.id && editingProgramField === 'mediumTermHours' ? (
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  value={editingProgramValue}
                                  onChange={(e) => setEditingProgramValue(parseInt(e.target.value) || 0)}
                                  onKeyDown={(e) => handleProgramEditKeyDown(e, record.id, 'mediumTermHours', editingProgramValue)}
                                  onBlur={() => saveEditProgram(record.id, 'mediumTermHours', editingProgramValue)}
                                  className="w-20 border rounded px-2 py-1 mr-1"
                                  min="0"
                                  autoFocus
                                />
                                <SaveOutlined 
                                  className="text-green-500 cursor-pointer mx-1" 
                                  onClick={() => saveEditProgram(record.id, 'mediumTermHours', editingProgramValue)}
                                />
                                <CloseOutlined 
                                  className="text-red-500 cursor-pointer" 
                                  onClick={() => {
                                    setEditingProgram(null);
                                    setEditingProgramField(null);
                                    setEditingProgramValue(null);
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className={`cursor-pointer hover:bg-gray-100 text-center ${record.isFromClass ? "text-blue-600" : ""}`}
                                onClick={() => startEditProgram(record, 'mediumTermHours')}
                              >
                                {text}
                              </div>
                            )
                          )
                        },
                        {
                          title: '장기(시간)',
                          dataIndex: 'longTermHours',
                          key: 'longTermHours',
                          width: 100,
                          render: (text, record) => (
                            editingProgram?.id === record.id && editingProgramField === 'longTermHours' ? (
                              <div className="flex items-center">
                                <input
                                  type="number"
                                  value={editingProgramValue}
                                  onChange={(e) => setEditingProgramValue(parseInt(e.target.value) || 0)}
                                  onKeyDown={(e) => handleProgramEditKeyDown(e, record.id, 'longTermHours', editingProgramValue)}
                                  onBlur={() => saveEditProgram(record.id, 'longTermHours', editingProgramValue)}
                                  className="w-20 border rounded px-2 py-1 mr-1"
                                  min="0"
                                  autoFocus
                                />
                                <SaveOutlined 
                                  className="text-green-500 cursor-pointer mx-1" 
                                  onClick={() => saveEditProgram(record.id, 'longTermHours', editingProgramValue)}
                                />
                                <CloseOutlined 
                                  className="text-red-500 cursor-pointer" 
                                  onClick={() => {
                                    setEditingProgram(null);
                                    setEditingProgramField(null);
                                    setEditingProgramValue(null);
                                  }}
                                />
                              </div>
                            ) : (
                              <div
                                className={`cursor-pointer hover:bg-gray-100 text-center ${record.isFromClass ? "text-blue-600" : ""}`}
                                onClick={() => startEditProgram(record, 'longTermHours')}
                              >
                                {text}
                              </div>
                            )
                          )
                        },
                        {
                          title: '관리',
                          key: 'action',
                          width: 120,
                          render: (_, record) => (
                            <div className="flex space-x-2">
                              {/* <Button
                                type="primary"
                                size="small"
                                icon={<SwapOutlined />}
                                onClick={(e) => openClassLinkModal(record, e)}
                              >
                                강의 관리
                              </Button> */}
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => {
                                  if (record.isFromClass) {
                                    toast.info('강의에서 가져온 데이터는 강의 관리에서 삭제해주세요.');
                                    return;
                                  }
                                  if(window.confirm(`'${record.name}' 프로그램을 삭제하시겠습니까?`)) {
                                    deleteProgram(record.id);
                                  }
                                }}
                                size="small"
                                disabled={record.isFromClass}
                              />
                            </div>
                          )
                        }
                      ]}
                    />
                  </Card>
                ))}
                
                {getModulesByStructure(structure.name).length === 0 && (
                  <Empty description="등록된 모듈이 없습니다." />
                )}
              </div>
            </TabPane>
          ))}
        </Tabs>
      </div>

      {renderStructureForm()}
      {renderModuleForm()}
      {renderProgramForm()}
      {renderStudentManagementModal()}
      {renderClassLinkModal()}
      {renderAddClassToProgramModal()}
    </div>
  );
};

export default IntegratedClassManagement; 